import { Fragment, useEffect, useReducer, useRef, useState } from "react";
import { Button, Dropdown, Form, Modal, Tab, Nav } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import config from "../../../../../config.json";
// import styles
import "lightgallery/css/lightgallery.css";
import "lightgallery/css/lg-zoom.css";
import "lightgallery/css/lg-thumbnail.css";

import "../../../../i18n";
import { useTranslation } from "react-i18next";
import soundNotif from "../../../../assets/sounds/vibration_notif.mp3";
import profileImg from "../../../../assets/images/profile/profile.png";
// import profile from "../../../../assets/logo.png";
import PageTitle from "../../../layouts/PageTitle";
import {
  fetchRestaurantData,
  fetchRestoDataByUser,
  updateUserData,
  userProfile,
} from "../../../../services/AuthService";
import { toast } from "react-toastify";
import StorytellingStepper from "./StorytellingStepper";
import WelcomeVideoSliceModal, {
  formatVideoClock,
  readVideoDuration,
} from "./WelcomeVideoSliceModal";
import JoinFranchiseSection from "./JoinFranchiseSection";
import { isFranchiseImpersonationTab } from "../../../../utils/franchiseSession";
import { getRestaurantId, setTabAuthMeta } from "../../../../utils/authToken";
import {
  createEmptyStorytellingEntry,
  normalizeStorytellingDisplay,
  normalizeStorytellingList,
  reindexStorytellingMap,
} from "./storytellingUtils";
import {
  deleteRestaurantLogo,
  updateRestaurantLogo,
  updateRestaurantWifiQR,
  deleteRestaurantWelcomeVideo,
  updateRestaurantWelcomeVideo,
  deleteRestaurantWelcomePhoto,
  updateRestaurantWelcomePhoto,
  deleteRestaurantWelcomeBackgroundVideo,
  updateRestaurantWelcomeBackgroundVideo,
  updateRestaurantStorytellingImage,
  deleteRestaurantStorytellingImage,
  updateRestoData,
} from "../../../../services/restaurantService";
import TableShapeIllustration, {
  DEFAULT_TABLE_COLOR,
} from "../../Dashboard/TableShapeIllustration";
import axios from "axios";
import { useLocation } from "react-router-dom";
import {
  FaUser,
  FaStore,
  FaCog,
  FaCamera,
  FaPencilAlt,
  FaTrash,
  FaInfoCircle,
  FaCalendarAlt,
  FaLanguage,
  FaWifi,
  FaLink,
  FaShareAlt,
  FaClock,
  FaChevronDown,
  FaLock,
  FaBookOpen,
  FaMobileAlt,
  FaExchangeAlt,
  FaGlobe,
  FaMapMarkerAlt,
  FaPhone,
  FaWhatsapp,
} from "react-icons/fa";
import { updateImageUser } from "../../../../services/UserService";
import Swal from "sweetalert2";
import { Colors } from "../../../../contants/Colors";
import TimePicker from "../../Forms/Pickers/TimePicker";
import "./AppProfile.css";
import { setRestaurantCurrencyCode, setRestaurantSecondaryCurrencyCode } from "../../../../utils/currency";
import CurrencySelect from "./CurrencySelect";
import CountryPhoneInput from "../../shared/CountryPhoneInput";
import ClientLanguagesSortableList from "./ClientLanguagesSortableList";
import { getMenuLanguageFlag } from "../../../../constants/menuLanguages";
import TwoFactorSettings from "./TwoFactorSettings";
import PasswordStrengthMeter from "../../PasswordStrengthMeter";
import {
  VisualChoiceGrid,
  ClientMenuLivePreview,
  ToggleVisualAid,
} from "./SettingsVisualAid";

/** Ensures a valid #RRGGBB for <input type="color" /> (browser requirement). */
function normalizeHexForColorInput(hex, fallback = "#F47F72") {
  if (hex == null || typeof hex !== "string") return fallback;
  let h = hex.trim();
  if (!h.startsWith("#")) h = `#${h}`;
  if (/^#[0-9A-F]{3}$/i.test(h)) {
    const r = h[1];
    const g = h[2];
    const b = h[3];
    h = `#${r}${r}${g}${g}${b}${b}`;
  }
  if (/^#[0-9A-F]{6}$/i.test(h)) return h.toUpperCase();
  return fallback;
}

/** Codes supported for the customer app language selector (order in DB = display order). */
const ALL_CLIENT_LANG_CODES = [
  "en",
  "fr",
  "de",
  "es",
  "it",
  "pt",
  "ar",
  "zh",
  "ja",
  "ru",
];

const MAX_SHIFTS_PER_DAY = 4;

function getRestaurantUploadUrl(folder, filename) {
  if (!filename) return null;
  const encoded = String(filename)
    .split("/")
    .filter(Boolean)
    .map((part) => encodeURIComponent(part))
    .join("/");
  const bases = [
    config.dev ? `${String(config.dev).replace(/\/+$/, "")}/uploads` : null,
    config.UPLOADS_SERVER_QR
      ? `${String(config.UPLOADS_SERVER_QR).replace(/\/+$/, "")}/uploads`
      : null,
    config.UPLOADS_SERVER,
  ].filter(Boolean);
  const base = bases.find(Boolean);
  return base ? `${base.replace(/\/+$/, "")}/${folder}/${encoded}` : null;
}

const POLICY_ICON_OPTIONS = [
  "👔",
  "👗",
  "👞",
  "🧥",
  "🧢",
  "🩳",
  "🩴",
  "🎩",
  "✨",
  "🚫",
  "✅",
  "⚠️",
];

const normalizePolicyItems = (items) =>
  Array.isArray(items)
    ? items
        .filter((item) => String(item?.label || "").trim())
        .map((item) => ({
          icon: item?.icon || "✅",
          label: String(item.label).trim(),
        }))
    : [];

/** Merge API workHours with default week + up to MAX_SHIFTS_PER_DAY shifts per day. */
function normalizeRestaurantWorkHours(rawWorkHours) {
  const defaultDays = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];
  return defaultDays.map((dayName) => {
    const existing = rawWorkHours?.find((w) => w.day === dayName);
    const startFallback = existing?.startTime || "09:00";
    const endFallback = existing?.endTime || "17:00";
    let shifts =
      existing?.shifts &&
      Array.isArray(existing.shifts) &&
      existing.shifts.length > 0
        ? existing.shifts.slice(0, MAX_SHIFTS_PER_DAY).map((s) => ({
            startTime: s?.startTime || startFallback,
            endTime: s?.endTime || endFallback,
          }))
        : [{ startTime: startFallback, endTime: endFallback }];
    return {
      day: dayName,
      isOpen: Boolean(existing?.isOpen),
      startTime: shifts[0].startTime,
      endTime: shifts[0].endTime,
      shifts,
    };
  });
}

/** Formats ISO date for profile "created at" display (locale-aware). */
function formatIsoDateTime(iso, locale) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  try {
    return new Intl.DateTimeFormat(locale || "en", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(d);
  } catch {
    return d.toISOString().slice(0, 16).replace("T", " ");
  }
}

function formatIsoDate(iso, locale) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  try {
    return new Intl.DateTimeFormat(locale || "en", {
      year: "numeric",
      month: "long",
      day: "numeric",
    }).format(d);
  } catch {
    return d.toISOString().slice(0, 10);
  }
}

const WEEKDAY_UTC_DATES = {
  Monday: "2024-01-01",
  Tuesday: "2024-01-02",
  Wednesday: "2024-01-03",
  Thursday: "2024-01-04",
  Friday: "2024-01-05",
  Saturday: "2024-01-06",
  Sunday: "2024-01-07",
};

function weekdayShortLabel(dayKey, locale) {
  const iso = WEEKDAY_UTC_DATES[dayKey];
  if (!iso) return String(dayKey || "").slice(0, 3);
  try {
    return new Intl.DateTimeFormat(locale || "en", {
      weekday: "short",
      timeZone: "UTC",
    }).format(new Date(`${iso}T12:00:00Z`));
  } catch {
    return String(dayKey).slice(0, 3);
  }
}

function formatScheduleClock(raw, timeFormat) {
  const [hRaw = "00", mRaw = "00"] = String(raw || "00:00").split(":");
  const hour = Math.max(0, Math.min(23, Number(hRaw) || 0));
  const minute = Math.max(0, Math.min(59, Number(mRaw) || 0));
  const mm = String(minute).padStart(2, "0");
  if (timeFormat !== "12h") {
    return `${String(hour).padStart(2, "0")}:${mm}`;
  }
  const period = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 || 12;
  return `${hour12}:${mm} ${period}`;
}

function getDayShifts(dayObj) {
  if (dayObj?.shifts?.length > 0) return dayObj.shifts;
  return [{ startTime: dayObj?.startTime, endTime: dayObj?.endTime }];
}

function formatDayHoursPreview(dayObj, timeFormat) {
  if (!dayObj?.isOpen) return "";
  return getDayShifts(dayObj)
    .map(
      (shift) =>
        `${formatScheduleClock(shift?.startTime, timeFormat)} – ${formatScheduleClock(
          shift?.endTime,
          timeFormat
        )}`
    )
    .join(" · ");
}

function LinkToggleRow({ id, label, checked, onCheckedChange, children }) {
  return (
    <div className={`link-toggle-row${checked ? " is-on" : ""}`}>
      <div className="link-toggle-row__head">
        <label className="link-toggle-row__label" htmlFor={id}>
          {label}
        </label>
        <div className="form-check form-switch m-0">
          <input
            id={id}
            className="form-check-input"
            type="checkbox"
            checked={Boolean(checked)}
            onChange={(e) => onCheckedChange(e.target.checked)}
          />
        </div>
      </div>
      {checked && children ? (
        <div className="link-toggle-row__body">{children}</div>
      ) : null}
    </div>
  );
}

/** Framed read-only block for “account / restaurant created” metadata. */
const profileMetaPanelStyle = {
  border: "1px solid rgba(28, 25, 22, 0.08)",
  borderRadius: 12,
  padding: "12px 14px",
  background: "#faf7f3",
  maxWidth: 360,
};

function getClientLanguagesEnabled(restaurantData) {
  const raw = restaurantData?.enabledLanguages;
  const fallback = [...ALL_CLIENT_LANG_CODES];
  if (!raw || raw.length === 0) return fallback;
  const filtered = raw.filter((c) => ALL_CLIENT_LANG_CODES.includes(c));
  return filtered.length > 0 ? filtered : fallback;
}

const initialState = false;
const reducer = (state, action) => {
  switch (action.type) {
    case "sendMessage":
      return { ...state, sendMessage: !state.sendMessage };
    case "postModal":
      return { ...state, post: !state.post };
    case "linkModal":
      return { ...state, link: !state.link };
    case "cameraModal":
      return { ...state, camera: !state.camera };
    case "replyModal":
      return { ...state, reply: !state.reply };
    default:
      return state;
  }
};

function separateWrittenAddress(restaurant) {
  const rawAddress = String(restaurant?.address || "").trim();
  const rawFull = String(restaurant?.fullAddress || "").trim();
  const mapsLink = /^https:\/\//i.test(rawAddress) ? rawAddress : "";
  const fullAddress =
    (rawFull && !/^https:\/\//i.test(rawFull) ? rawFull : "") ||
    (rawAddress && !mapsLink ? rawAddress : "");
  return { fullAddress, address: mapsLink };
}

const AppProfile = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const tabParam = params.get("tab");
  const sectionParam = params.get("section");
  const franchiseManagingRestaurant = isFranchiseImpersonationTab();
  const [activeMainTab, setActiveMainTab] = useState(
    tabParam === "restaurant" || franchiseManagingRestaurant
      ? "Brand Information"
      : "Owner Information"
  );
  const [activeRestoSubTab, setActiveRestoSubTab] = useState(
    sectionParam === "storytelling" ? "Storytelling" : "General"
  );
  const [state, dispatch] = useReducer(reducer, initialState);
  const [user, setUser] = useState(null);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [restaurantData, setRestaurantData] = useState(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneWhatsapp, setPhoneWhatsapp] = useState("");
  const [message, setMessage] = useState(null); // pour le message de retour
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [selectedLogo, setSelectedLogo] = useState(null);
  const [previewLogo, setPreviewLogo] = useState(null);
  const logoInputRef = useRef(null);
  const [notificationSoundEnabled, setNotificationSoundEnabled] =
    useState(false);
  const notificationSound = useRef(new Audio(soundNotif));
  const [previewWifiQR, setPreviewWifiQR] = useState(null);
  const [selectedWifiQR, setSelectedWifiQR] = useState(null);
  const [previewWelcomeVideo, setPreviewWelcomeVideo] = useState(null);
  const [selectedWelcomeVideo, setSelectedWelcomeVideo] = useState(null);
  const welcomeVideoInputRef = useRef(null);
  const [previewWelcomePhoto, setPreviewWelcomePhoto] = useState(null);
  const [selectedWelcomePhoto, setSelectedWelcomePhoto] = useState(null);
  const welcomePhotoInputRef = useRef(null);
  const [previewWelcomeBackgroundVideo, setPreviewWelcomeBackgroundVideo] =
    useState(null);
  const [selectedWelcomeBackgroundVideo, setSelectedWelcomeBackgroundVideo] =
    useState(null);
  const welcomeBackgroundVideoInputRef = useRef(null);
  const [welcomeVideoStartSec, setWelcomeVideoStartSec] = useState(0);
  const [welcomeVideoEndSec, setWelcomeVideoEndSec] = useState(0);
  const [backgroundVideoStartSec, setBackgroundVideoStartSec] = useState(0);
  const [backgroundVideoEndSec, setBackgroundVideoEndSec] = useState(0);
  const [videoSlice, setVideoSlice] = useState(null);
  const [previewStorytellingImages, setPreviewStorytellingImages] = useState({});
  const [pendingStorytellingImages, setPendingStorytellingImages] = useState({});
  const [storytellingSubmitting, setStorytellingSubmitting] = useState(false);
  const [showOtherLanguages, setShowOtherLanguages] = useState(false);
  const [showExtraTaxOptions, setShowExtraTaxOptions] = useState(false);
  const [livePreviewPage, setLivePreviewPage] = useState("welcome");
  const [linksVisibility, setLinksVisibility] = useState({
    address: false,
    contactEmail: false,
    googleReviewLink: false,
    instagramLink: false,
    facebookLink: false,
    tiktokLink: false,
    twitterLink: false,
  });

  /** Per weekday key (e.g. "Monday"): `true` = hours visible. Default collapsed (day + status only). */
  const [scheduleDayExpanded, setScheduleDayExpanded] = useState({});
  const activeTimeFormat = restaurantData?.timeFormat === "12h" ? "12h" : "24h";
  const scheduleOpenDaysCount =
    restaurantData?.workHours?.filter((d) => d.isOpen).length ?? 0;

  // Open Restaurant Information tab when navigating from navbar with ?tab=restaurant
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    if (p.get("tab") === "restaurant") {
      setActiveMainTab("Brand Information");
      if (p.get("section") === "storytelling") {
        setActiveRestoSubTab("Storytelling");
      }
    }
  }, [location.search]);

  useEffect(() => {
    if (notificationSoundEnabled) {
      notificationSound.current.currentTime = 0; // repartir du début
      notificationSound.current.play().catch(() => {
        console.warn("Impossible de jouer le son : interaction requise");
      });
    }
  }, [notificationSoundEnabled]);

  useEffect(() => {
    if (user) {
      setFirstName(user.firstName);
      setLastName(user.lastName);
      setPhone(user.phone);
      setPhoneWhatsapp(user?.phoneWhatsapp);
    }
  }, [user]);

  useEffect(() => {
    fetchUserData();
  }, []);

  useEffect(() => {
    if (!franchiseManagingRestaurant) return;
    const sessionRestaurantId = getRestaurantId();
    if (!sessionRestaurantId) return;
    if (restaurantData?._id && String(restaurantData._id) === sessionRestaurantId) {
      return;
    }
    fetchUserData();
  }, [franchiseManagingRestaurant, restaurantData?._id]);

  useEffect(() => {
    if (!restaurantData?._id) return;
    setLinksVisibility({
      address: Boolean(restaurantData?.address),
      contactEmail: Boolean(
        restaurantData?.contactEmail &&
          String(restaurantData.contactEmail).trim() !== ""
      ),
      googleReviewLink: Boolean(restaurantData?.googleReviewLink),
      instagramLink: Boolean(restaurantData?.instagramLink),
      facebookLink: Boolean(restaurantData?.facebookLink),
      tiktokLink: Boolean(restaurantData?.tiktokLink),
      twitterLink: Boolean(restaurantData?.twitterLink),
    });
  }, [restaurantData?._id]);

  const isScheduleDayBodyVisible = (dayKey) => scheduleDayExpanded[dayKey] === true;

  const toggleScheduleDayBody = (dayKey) => {
    setScheduleDayExpanded((prev) => ({
      ...prev,
      [dayKey]: !(prev[dayKey] === true),
    }));
  };

  const handleDayOpenChange = (index, isOpen) => {
    setRestaurantData((prev) => {
      if (!prev?.workHours) return prev;
      const newWorkHours = prev.workHours.map((d, i) =>
        i === index ? { ...d, isOpen } : d
      );
      return { ...prev, workHours: newWorkHours };
    });
  };

  const handleShiftTimeChange = (dayIndex, shiftIndex, field, value) => {
    setRestaurantData((prev) => {
      if (!prev?.workHours) return prev;
      const newWorkHours = prev.workHours.map((d, i) => {
        if (i !== dayIndex) return d;
        const shifts = (d.shifts || [{ startTime: d.startTime, endTime: d.endTime }]).map(
          (s, si) => (si === shiftIndex ? { ...s, [field]: value } : s)
        );
        const next = { ...d, shifts };
        if (shiftIndex === 0) {
          if (field === "startTime") next.startTime = value;
          if (field === "endTime") next.endTime = value;
        }
        return next;
      });
      return { ...prev, workHours: newWorkHours };
    });
  };

  const handleShiftCountChange = (dayIndex, count) => {
    const n = Math.min(MAX_SHIFTS_PER_DAY, Math.max(1, Number(count) || 1));
    setRestaurantData((prev) => {
      if (!prev?.workHours) return prev;
      const newWorkHours = prev.workHours.map((d, i) => {
        if (i !== dayIndex) return d;
        let shifts = [...(d.shifts?.length ? d.shifts : [{ startTime: d.startTime, endTime: d.endTime }])];
        while (shifts.length < n) {
          const last = shifts[shifts.length - 1];
          shifts.push({
            startTime: last?.endTime || "12:00",
            endTime: "17:00",
          });
        }
        shifts = shifts.slice(0, n);
        return {
          ...d,
          shifts,
          startTime: shifts[0].startTime,
          endTime: shifts[0].endTime,
        };
      });
      return { ...prev, workHours: newWorkHours };
    });
  };

  const handleSetAllDaysOpenState = (isOpen) => {
    setRestaurantData((prev) => {
      if (!prev?.workHours) return prev;
      const newWorkHours = prev.workHours.map((d) => ({ ...d, isOpen }));
      return { ...prev, workHours: newWorkHours };
    });
  };

  const handleApplyMondayToAllDays = () => {
    setRestaurantData((prev) => {
      if (!prev?.workHours?.length) return prev;
      const monday = prev.workHours[0];
      if (!monday) return prev;
      const mondayShifts =
        monday.shifts?.length > 0
          ? monday.shifts.map((s) => ({ ...s }))
          : [{ startTime: monday.startTime, endTime: monday.endTime }];
      const newWorkHours = prev.workHours.map((d) => ({
        ...d,
        isOpen: monday.isOpen,
        shifts: mondayShifts.map((s) => ({ ...s })),
        startTime: mondayShifts[0]?.startTime || "09:00",
        endTime: mondayShifts[0]?.endTime || "17:00",
      }));
      return { ...prev, workHours: newWorkHours };
    });
    toast.success(t("scheduleCopiedToWeek"));
  };

  const updateDressCodeField = (field, value) => {
    setRestaurantData((prev) => ({
      ...prev,
      dressCode: {
        ...(prev?.dressCode || {}),
        [field]: value,
      },
    }));
  };

  const addDressCodeItem = (listKey) => {
    setRestaurantData((prev) => ({
      ...prev,
      dressCode: {
        ...(prev?.dressCode || {}),
        [listKey]: [
          ...(prev?.dressCode?.[listKey] || []),
          { icon: listKey === "forbiddenItems" ? "🚫" : "✅", label: "" },
        ],
      },
    }));
  };

  const updateDressCodeItem = (listKey, index, field, value) => {
    setRestaurantData((prev) => ({
      ...prev,
      dressCode: {
        ...(prev?.dressCode || {}),
        [listKey]: (prev?.dressCode?.[listKey] || []).map((item, i) =>
          i === index ? { ...item, [field]: value } : item
        ),
      },
    }));
  };

  const removeDressCodeItem = (listKey, index) => {
    setRestaurantData((prev) => ({
      ...prev,
      dressCode: {
        ...(prev?.dressCode || {}),
        [listKey]: (prev?.dressCode?.[listKey] || []).filter(
          (_, i) => i !== index
        ),
      },
    }));
  };

  const updateStorytellingField = (index, field, value) => {
    setRestaurantData((prev) => {
      const list = [...normalizeStorytellingList(prev?.storytelling)];
      list[index] = { ...list[index], [field]: value };
      return { ...prev, storytelling: list };
    });
  };

  const addStorytellingEntry = () => {
    setRestaurantData((prev) => ({
      ...prev,
      storytelling: [
        ...normalizeStorytellingList(prev?.storytelling),
        createEmptyStorytellingEntry(),
      ],
    }));
  };

  const removeStorytellingEntry = (index) => {
    setRestaurantData((prev) => {
      const list = normalizeStorytellingList(prev?.storytelling).filter(
        (_, itemIndex) => itemIndex !== index
      );
      return {
        ...prev,
        storytelling: list.length ? list : [createEmptyStorytellingEntry()],
      };
    });
    setPendingStorytellingImages((prev) => reindexStorytellingMap(prev, index));
    setPreviewStorytellingImages((prev) => reindexStorytellingMap(prev, index));
  };

  const handleStorytellingImageChange = (index, e) => {
    const file = e.target.files?.[0];
    if (file) {
      setPendingStorytellingImages((prev) => ({ ...prev, [index]: file }));
      setPreviewStorytellingImages((prev) => ({
        ...prev,
        [index]: URL.createObjectURL(file),
      }));
    }
  };

  const handleDeleteStorytellingImage = async (index) => {
    const entry = normalizeStorytellingList(restaurantData?.storytelling)[index];
    const result = await Swal.fire({
      title: t("deleteStorytellingImage"),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: t("confirm"),
      cancelButtonText: t("cancel"),
    });
    if (!result.isConfirmed) return;
    try {
      if (entry?.image) {
        await deleteRestaurantStorytellingImage(index);
      }
      setRestaurantData((prev) => {
        const list = [...normalizeStorytellingList(prev?.storytelling)];
        list[index] = { ...list[index], image: null };
        return { ...prev, storytelling: list };
      });
      setPendingStorytellingImages((prev) => {
        const next = { ...prev };
        delete next[index];
        return next;
      });
      setPreviewStorytellingImages((prev) => {
        const next = { ...prev };
        delete next[index];
        return next;
      });
      toast.success(t("storytellingImageDeleted"));
    } catch (error) {
      console.error(error);
      toast.error(error?.message || t("errorRestoDataUpdated"));
    }
  };

  const handleSubmitStorytellingChanges = async () => {
    try {
      if (!restaurantData) return;
      setStorytellingSubmitting(true);

      const storytellingToSave = normalizeStorytellingList(
        restaurantData.storytelling
      ).map((item) => ({
        question: String(item.question || "").trim(),
        description: String(item.description || "").trim(),
        image: item.image || null,
        chapterLabel: String(item.chapterLabel || "").trim(),
      }));

      const storytellingDisplayToSave = normalizeStorytellingDisplay(
        restaurantData.storytellingDisplay,
        restaurantData?.menuColors?.buttonColor
      );

      const response = await updateRestoData({
        storytelling: storytellingToSave,
        storytellingDisplay: storytellingDisplayToSave,
      });

      const savedStorytellingDisplay = normalizeStorytellingDisplay(
        response?.restaurant?.storytellingDisplay ?? storytellingDisplayToSave,
        restaurantData?.menuColors?.buttonColor
      );

      let updatedList = [...storytellingToSave];
      const pendingEntries = Object.entries(pendingStorytellingImages);

      for (const [indexStr, file] of pendingEntries) {
        const index = Number(indexStr);
        const imageRes = await updateRestaurantStorytellingImage(file, index);
        if (imageRes?.storytellingImage) {
          updatedList[index] = {
            ...updatedList[index],
            image: imageRes.storytellingImage,
          };
        }
      }

      setRestaurantData((prev) => ({
        ...prev,
        storytelling: normalizeStorytellingList(
          response?.restaurant?.storytelling ?? updatedList
        ),
        storytellingDisplay: savedStorytellingDisplay,
      }));
      setPendingStorytellingImages({});
      setPreviewStorytellingImages({});
      toast.success(t("restoDataUpdated"));
    } catch (error) {
      console.error(error);
      toast.error(error?.message || t("errorRestoDataUpdated"));
    } finally {
      setStorytellingSubmitting(false);
    }
  };

  const fetchUserData = async () => {
    try {
      const userData = await userProfile();
      setUser(userData);
      setTwoFactorEnabled(Boolean(userData?.twoFactorEnabled));
      console.log("user data", userData.image);

      const restoData = await fetchRestoDataByUser();
      const separatedAddress = separateWrittenAddress(restoData.restaurant);
      setRestaurantData({
        ...restoData.restaurant,
        ...separatedAddress,
        timeFormat: restoData?.restaurant?.timeFormat === "12h" ? "12h" : "24h",
        workHours: normalizeRestaurantWorkHours(restoData.restaurant?.workHours),
        storytelling: normalizeStorytellingList(restoData.restaurant?.storytelling),
        storytellingDisplay: normalizeStorytellingDisplay(
          restoData.restaurant?.storytellingDisplay,
          restoData.restaurant?.menuColors?.buttonColor
        ),
      });
      setPendingStorytellingImages({});
      setPreviewStorytellingImages({});
      setRestaurantCurrencyCode(restoData?.restaurant?.currency);
      setRestaurantSecondaryCurrencyCode(restoData?.restaurant?.secondaryCurrency);

      if (restoData?.restaurant?._id) {
        setTabAuthMeta({
          restaurantId: String(restoData.restaurant._id),
          restaurantCurrency: restoData?.restaurant?.currency || "",
          restaurantSecondaryCurrency:
            restoData?.restaurant?.secondaryCurrency || "",
        });
      }

    } catch (error) {
      console.log(error);
    }
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    setError(null);
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();

    // Validation rapide côté client
    if (!oldPassword || !password || !confirmPassword) {
      setError("All fields are required.");
      return;
    }

    if (password !== confirmPassword) {
      setError("The passwords do not match.");
      return;
    }

    setLoading(true);
    setError(null);
    setMessage(null);

    try {
      const id = user._id;
      console.log("id id", id);
      const response = await axios.put(
        `${config.dev}/user/updatepassword/${id}`,
        {
          oldPassword,
          password,
          confirmPassword,
        }
      );

      setMessage(response.data.message);
      toast.success(response.data.message);
      setOldPassword("");
      setPassword("");
      setConfirmPassword("");
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || "Update password error";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUserData = async () => {
    const message = await updateUserData({
      firstName: firstName || user.firstName,
      lastName: lastName || user.lastName,
      phone: phone || user.phone,
      phoneWhatsapp:
        phoneWhatsapp !== undefined ? phoneWhatsapp : user.phoneWhatsapp,
    });

    if (message === "updated") {
      toast.success(t("userDataUpdated"));
      await fetchUserData();
    } else {
      toast.error(t("errorUserDataUpdate"));
    }
  };

  const handleSubmitRestaurantChanges = async () => {
    try {
      if (!restaurantData) {
        toast.error("Restaurant data not loaded yet. Please wait a few seconds and try again.");
        return;
      }

      const address = String(restaurantData?.address || "").trim();
      const writtenAddress = String(restaurantData?.fullAddress || "").trim();
      if (/^https?:\/\//i.test(writtenAddress)) {
        toast.error(
          t("writtenFullAddressNotLink", {
            defaultValue:
              "Full address is the written street address, not a Google Maps link.",
          })
        );
        return;
      }
      if (
        linksVisibility.address &&
        address.length > 0 &&
        !address.startsWith("https://")
      ) {
        toast.error(t("fullAddressError"));
        return;
      }
      const googleReviewLink = String(
        restaurantData?.googleReviewLink || ""
      ).trim();
      if (
        linksVisibility.googleReviewLink &&
        googleReviewLink.length > 0 &&
        !googleReviewLink.startsWith("https://")
      ) {
        toast.error(t("googleReviewLinkError"));
        return;
      }

      console.log("google link", restaurantData.googleReviewLink);

      // 🔹 Normaliser msgBienvenue (toutes les langues client gérées)
      const msgBienvenueToSave = ALL_CLIENT_LANG_CODES.map((lang) => {
        const existing = restaurantData.msgBienvenue?.find(
          (m) => m.language === lang
        );
        return {
          language: lang,
          msg: existing?.msg || "",
        };
      });

      // 🔹 Normaliser workHours
      const defaultDays = [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
        "Sunday",
      ];
      const workHoursToSave = defaultDays.map((day) => {
        const existing = restaurantData.workHours?.find((w) => w.day === day);
        let shifts =
          existing?.shifts && existing.shifts.length > 0
            ? existing.shifts
            : [
                {
                  startTime: existing?.startTime || "09:00",
                  endTime: existing?.endTime || "17:00",
                },
              ];
        shifts = shifts.slice(0, MAX_SHIFTS_PER_DAY).map((s) => ({
          startTime: s?.startTime || "09:00",
          endTime: s?.endTime || "17:00",
        }));
        const first = shifts[0];
        return {
          day,
          isOpen: existing?.isOpen || false,
          startTime: first.startTime,
          endTime: first.endTime,
          shifts,
        };
      });

      if (
        linksVisibility.contactEmail &&
        String(restaurantData.contactEmail || "").trim() !== "" &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
          String(restaurantData.contactEmail).trim()
        )
      ) {
        toast.error(t("invalidEmail"));
        return;
      }

      // 🔹 Mise à jour des données du restaurant
      const enabledLanguagesToSave =
        restaurantData.enabledLanguages?.length > 0
          ? restaurantData.enabledLanguages
          : [...ALL_CLIENT_LANG_CODES];
      const getLinksValue = (isEnabled, value) =>
        isEnabled ? value || "" : "";
      await updateRestoData({
        nameRes: restaurantData.nameRes,
        fullAddress: String(restaurantData.fullAddress || "").trim(),
        address: getLinksValue(linksVisibility.address, restaurantData.address),
        serviceType: restaurantData.serviceType,
        percentages: restaurantData.percentages,
        wifiUser: restaurantData.wifiUser,
        wifiPwd: restaurantData.wifiPwd,
        msgBienvenue: msgBienvenueToSave,
        clientMenuShowLogo: restaurantData.clientMenuShowLogo !== false,
        clientWelcomeScreenEnabled:
          restaurantData.clientWelcomeScreenEnabled !== false,
        clientWelcomeVideoEnabled:
          restaurantData.clientWelcomeVideoEnabled !== false,
        clientShowAllMenusOnOnePage:
          restaurantData.clientShowAllMenusOnOnePage === true,
        clientShowClaimButton: restaurantData.clientShowClaimButton !== false,
        clientShowWelcomeContactButton:
          restaurantData.clientShowWelcomeContactButton !== false,
        clientShowStorytellingButton:
          restaurantData.clientShowStorytellingButton !== false,
        clientMenuVoiceEnabled: restaurantData.clientMenuVoiceEnabled === true,
        clientMenuVoiceMode:
          restaurantData.clientMenuVoiceMode === "auto_product"
            ? "auto_product"
            : "on_click",
        clientMenuVoiceIncludeDescription:
          restaurantData.clientMenuVoiceIncludeDescription === true,
        clientShowEventsButton:
          restaurantData.clientShowEventsButton !== false,
        clientShowReservationButton:
          restaurantData.clientShowReservationButton !== false,
        dressCode: {
          description: String(restaurantData?.dressCode?.description || "").trim(),
          requiredItems: normalizePolicyItems(
            restaurantData?.dressCode?.requiredItems
          ),
          forbiddenItems: normalizePolicyItems(
            restaurantData?.dressCode?.forbiddenItems
          ),
        },
        restaurantPolicies: String(
          restaurantData?.restaurantPolicies || ""
        ).trim(),
        refundPolicies: String(restaurantData?.refundPolicies || "").trim(),
        menuMode: restaurantData.menuMode || "view",
        menuColors: restaurantData.menuColors || {},
        enabledLanguages: enabledLanguagesToSave,
        workHours: workHoursToSave,
        timeFormat: restaurantData?.timeFormat === "12h" ? "12h" : "24h",
        city: restaurantData.city,
        latitude: restaurantData?.latitude ?? null,
        longitude: restaurantData?.longitude ?? null,
        googleReviewLink: getLinksValue(
          linksVisibility.googleReviewLink,
          restaurantData?.googleReviewLink
        ),
        currency: restaurantData?.currency,
        secondaryCurrency: restaurantData?.secondaryCurrency || "",
        contactEmail: linksVisibility.contactEmail
          ? (String(restaurantData?.contactEmail || "").trim() || null)
          : null,
        instagramLink: getLinksValue(
          linksVisibility.instagramLink,
          restaurantData?.instagramLink
        ),
        facebookLink: getLinksValue(
          linksVisibility.facebookLink,
          restaurantData?.facebookLink
        ),
        tiktokLink: getLinksValue(
          linksVisibility.tiktokLink,
          restaurantData?.tiktokLink
        ),
        twitterLink: getLinksValue(
          linksVisibility.twitterLink,
          restaurantData?.twitterLink
        ),

        NIF:
          restaurantData?.country?.toLowerCase() === "portugais"
            ? restaurantData.NIF
            : undefined,
        taxIdentification:
          restaurantData?.country?.toLowerCase() !== "portugais"
            ? restaurantData.taxIdentification
            : undefined,
        taxStyle: restaurantData.taxStyle || null,
        storytelling: normalizeStorytellingList(restaurantData?.storytelling).map(
          (item) => ({
            question: String(item.question || "").trim(),
            description: String(item.description || "").trim(),
            image: item.image || null,
            chapterLabel: String(item.chapterLabel || "").trim(),
          })
        ),
        storytellingDisplay: normalizeStorytellingDisplay(
          restaurantData?.storytellingDisplay,
          restaurantData?.menuColors?.buttonColor
        ),
      });

      // Keep dashboard currency in sync with restaurant settings
      setRestaurantCurrencyCode(restaurantData?.currency);
      setRestaurantSecondaryCurrencyCode(restaurantData?.secondaryCurrency);

      // 🔹 Mise à jour du logo si présent
      if (selectedLogo) {
        await updateRestaurantLogo(selectedLogo);
      }

      // 🔹 Mise à jour du Wi-Fi QR si présent
      if (selectedWifiQR) {
        await updateRestaurantWifiQR(selectedWifiQR);
      }
      if (selectedWelcomeVideo) {
        toast.info(t("welcomeVideoProcessing"));
        const videoRes = await updateRestaurantWelcomeVideo(
          selectedWelcomeVideo,
          welcomeVideoStartSec,
          welcomeVideoEndSec
        );
        if (videoRes?.welcomeVideo) {
          setRestaurantData((prev) => ({
            ...prev,
            welcomeVideo: videoRes.welcomeVideo,
            clientWelcomeVideoEnabled: true,
          }));
        }
        setSelectedWelcomeVideo(null);
        setPreviewWelcomeVideo(null);
        setWelcomeVideoStartSec(0);
        setWelcomeVideoEndSec(0);
        if (welcomeVideoInputRef.current) {
          welcomeVideoInputRef.current.value = "";
        }
      }
      if (selectedWelcomePhoto) {
        const photoRes = await updateRestaurantWelcomePhoto(selectedWelcomePhoto);
        if (photoRes?.welcomePhoto) {
          setRestaurantData((prev) => ({
            ...prev,
            welcomePhoto: photoRes.welcomePhoto,
            welcomeBackgroundVideo: null,
          }));
        }
        setSelectedWelcomePhoto(null);
        setPreviewWelcomePhoto(null);
        setSelectedWelcomeBackgroundVideo(null);
        setPreviewWelcomeBackgroundVideo(null);
        if (welcomePhotoInputRef.current) {
          welcomePhotoInputRef.current.value = "";
        }
        if (welcomeBackgroundVideoInputRef.current) {
          welcomeBackgroundVideoInputRef.current.value = "";
        }
      }
      if (selectedWelcomeBackgroundVideo) {
        toast.info(t("welcomeVideoProcessing"));
        const bgVideoRes = await updateRestaurantWelcomeBackgroundVideo(
          selectedWelcomeBackgroundVideo,
          backgroundVideoStartSec,
          backgroundVideoEndSec
        );
        if (bgVideoRes?.welcomeBackgroundVideo) {
          setRestaurantData((prev) => ({
            ...prev,
            welcomeBackgroundVideo: bgVideoRes.welcomeBackgroundVideo,
            welcomePhoto: null,
          }));
        }
        setSelectedWelcomeBackgroundVideo(null);
        setPreviewWelcomeBackgroundVideo(null);
        setBackgroundVideoStartSec(0);
        setBackgroundVideoEndSec(0);
        setSelectedWelcomePhoto(null);
        setPreviewWelcomePhoto(null);
        if (welcomeBackgroundVideoInputRef.current) {
          welcomeBackgroundVideoInputRef.current.value = "";
        }
        if (welcomePhotoInputRef.current) {
          welcomePhotoInputRef.current.value = "";
        }
      }

      toast.success(t("restoDataUpdated"));
    } catch (error) {
      console.error(error);

      const backendMessage =
        typeof error === "string"
          ? error
          : error?.message
          ? error.message
          : typeof error?.error === "string"
          ? error.error
          : error?.response?.data?.message
          ? error.response.data.message
          : null;

      if (backendMessage) {
        toast.error(backendMessage);
        return;
      }

      // If we still can't extract a message, show something useful instead of silently failing.
      const fallback =
        error && typeof error === "object"
          ? JSON.stringify(error)
          : t("errorRestoDataUpdated");

      toast.error(fallback || t("errorRestoDataUpdated"));
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedLogo(file);
      setPreviewLogo(URL.createObjectURL(file));
    }
  };

  const applyVideoSlice = (kind, file, url, start, end) => {
    if (kind === "background") {
      setSelectedWelcomeBackgroundVideo(file);
      setPreviewWelcomeBackgroundVideo((current) => {
        if (current && current !== url) URL.revokeObjectURL(current);
        return url;
      });
      setBackgroundVideoStartSec(start);
      setBackgroundVideoEndSec(end);
      setSelectedWelcomePhoto(null);
      setPreviewWelcomePhoto(null);
      if (welcomePhotoInputRef.current) {
        welcomePhotoInputRef.current.value = "";
      }
      return;
    }

    setSelectedWelcomeVideo(file);
    setPreviewWelcomeVideo((current) => {
      if (current && current !== url) URL.revokeObjectURL(current);
      return url;
    });
    setWelcomeVideoStartSec(start);
    setWelcomeVideoEndSec(end);
  };

  const prepareWelcomeVideoFile = async (file, kind) => {
    const maxBytes = 40 * 1024 * 1024;
    if (file.size > maxBytes) {
      toast.error(t("welcomeVideoTooLarge"));
      return;
    }

    const url = URL.createObjectURL(file);
    try {
      const duration = await readVideoDuration(url);
      if (!Number.isFinite(duration) || duration <= 1.05) {
        applyVideoSlice(kind, file, url, 0, Number.isFinite(duration) ? duration : 0);
        return;
      }
      setVideoSlice({ file, url, duration, kind });
    } catch {
      URL.revokeObjectURL(url);
      toast.error(t("videoSliceUnreadable"));
    }
  };

  const handleWelcomeVideoChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    void prepareWelcomeVideoFile(file, "welcome");
  };

  const handleWelcomePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setSelectedWelcomePhoto(file);
    setPreviewWelcomePhoto(URL.createObjectURL(file));
    setSelectedWelcomeBackgroundVideo(null);
    setPreviewWelcomeBackgroundVideo(null);
    setBackgroundVideoStartSec(0);
    setBackgroundVideoEndSec(0);
    if (welcomeBackgroundVideoInputRef.current) {
      welcomeBackgroundVideoInputRef.current.value = "";
    }
  };

  const handleWelcomeBackgroundVideoChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    void prepareWelcomeVideoFile(file, "background");
  };

  const safeMenuColors = restaurantData?.menuColors || {
    buttonColor: "#f47f72",
    tableQRCode: "#f47f72",
  };
  // phone number : Autorise uniquement chiffres, + et espaces
  const handlePhoneWhatsappChange = (value) => {
    const regex = /^[0-9+ ]*$/;

    if (regex.test(value)) {
      setPhoneWhatsapp(value);
    }
  };

  const isRestaurantHeader = activeMainTab === "Brand Information";
  const restaurantTitle = restaurantData?.nameRes || t("restoInfo");
  const restaurantLocation = [restaurantData?.city, restaurantData?.country]
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .join(" · ");
  const personTitle =
    `${user?.firstName || ""} ${user?.lastName || ""}`.trim() || t("personalInfo");
  const restoLogoSrc =
    previewLogo ||
    (restaurantData?.logo
      ? `${config.UPLOADS_SERVER}/logos/${restaurantData.logo}`
      : null);
  const personAvatarSrc = user?.image
    ? `${config.UPLOADS_SERVER}/user/${user.image}`
    : profileImg;
  const headerEyebrowMap = {
    "Owner Information": t("personalInfo"),
    "Brand Information": t("restoInfo"),
    Setting: t("password"),
    TwoFactor: t("twoFactorAuthShort", { defaultValue: "2FA" }),
  };
  const headerEyebrow = headerEyebrowMap[activeMainTab] || t("restoInfo");
  const headerTitle = isRestaurantHeader ? restaurantTitle : personTitle;
  const headerMeta = isRestaurantHeader
    ? restaurantLocation
    : user?.email || "";
  const accountCreatedAt = user?.createdAt || user?.created_at;

  return (
    <Fragment>
      <PageTitle activeMenu={t("profile")} motherMenu={t("app")} />

      <div className="app-profile-page" data-tour="tour-page-profile">
      <div className="app-profile-shell">
                  <Tab.Container activeKey={activeMainTab} onSelect={(k) => setActiveMainTab(k)}>
                    <header className="app-profile-header">
                      <div className="app-profile-header__top">
                        <div className="app-profile-header__identity">
                          <div className="app-profile-header__avatar">
                            {isRestaurantHeader ? (
                              restoLogoSrc ? (
                                <img src={restoLogoSrc} alt="" />
                              ) : (
                                <span className="app-profile-header__avatar-fallback" aria-hidden>
                                  <FaStore size={26} />
                                </span>
                              )
                            ) : (
                              <>
                                <img src={personAvatarSrc} alt="" />
                                <label className="app-profile-header__camera">
                                  <FaCamera size={14} />
                                  <input
                                    type="file"
                                    accept="image/*"
                                    onChange={async (e) => {
                                      const file = e.target.files[0];
                                      if (!file) return;

                                      try {
                                        const data = await updateImageUser(file);
                                        setUser((prev) => ({
                                          ...prev,
                                          image: data.imageUrl.split("/").pop(),
                                        }));
                                        toast.success(t("profileUpdated"));
                                      } catch (err) {
                                        toast.error(
                                          err.message || "Erreur lors de la mise à jour"
                                        );
                                      }
                                    }}
                                  />
                                </label>
                              </>
                            )}
                          </div>
                          <div className="app-profile-header__copy">
                            <p className="app-profile-header__eyebrow">
                              {isRestaurantHeader ? <FaStore /> : <FaUser />}
                              {headerEyebrow}
                            </p>
                            <h1 className="app-profile-header__title">{headerTitle}</h1>
                            {headerMeta ? (
                              <p className="app-profile-header__meta">
                                {isRestaurantHeader ? (
                                  <FaMapMarkerAlt aria-hidden />
                                ) : null}
                                {headerMeta}
                              </p>
                            ) : null}
                          </div>
                        </div>
                      </div>
                      <Nav as="ul" className="nav nav-tabs app-profile-main-tabs">
                      <Nav.Item as="li" className="nav-item">
                        <Nav.Link
                          to="#owner-information"
                          eventKey="Owner Information"
                        >
                          <FaUser style={{ marginRight: 8 }} />
                          {t("personalInfo")}
                        </Nav.Link>
                      </Nav.Item>

                      <Nav.Item as="li" className="nav-item">
                        <Nav.Link
                          to="#brand-information"
                          eventKey="Brand Information"
                        >
                          <FaStore style={{ marginRight: 8 }} />
                          {t("restoInfo")}
                        </Nav.Link>
                      </Nav.Item>

                      <Nav.Item as="li" className="nav-item">
                        <Nav.Link to="#profile-settings" eventKey="Setting">
                          <FaLock style={{ marginRight: 8 }} />
                          {t("password")}
                        </Nav.Link>
                      </Nav.Item>

                      <Nav.Item as="li" className="nav-item">
                        <Nav.Link to="#profile-2fa" eventKey="TwoFactor">
                          <FaMobileAlt style={{ marginRight: 8 }} />
                          {t("twoFactorAuthShort", { defaultValue: "2FA" })}
                        </Nav.Link>
                      </Nav.Item>
                    </Nav>
                    </header>
                    <div className="app-profile-main">
                    <Tab.Content>
                      <Tab.Pane
                        id="#owner-information"
                        eventKey="Owner Information"
                      >
                        <div className="personal-info">
                          <div className="personal-info-pair">
                            <div className="personal-info-col">
                            <article className="resto-info-card personal-info-card">
                              <div className="resto-info-card__head">
                                <span className="resto-info-card__icon" aria-hidden>
                                  <FaUser />
                                </span>
                                <div>
                                  <h3>
                                    {t("personalInfoIdentity", {
                                      defaultValue: "Your name",
                                    })}
                                  </h3>
                                </div>
                              </div>
                              <div className="resto-info-card__body personal-info-fields">
                                <label className="personal-info-field" htmlFor="personal-first-name">
                                  <span className="form-label">{t("firstname")}</span>
                                  <input
                                    id="personal-first-name"
                                    type="text"
                                    className="form-control"
                                    autoComplete="given-name"
                                    value={firstName}
                                    onChange={(e) => setFirstName(e.target.value)}
                                  />
                                </label>
                                <label className="personal-info-field" htmlFor="personal-last-name">
                                  <span className="form-label">{t("lastname")}</span>
                                  <input
                                    id="personal-last-name"
                                    type="text"
                                    className="form-control"
                                    autoComplete="family-name"
                                    value={lastName}
                                    onChange={(e) => setLastName(e.target.value)}
                                  />
                                </label>
                              </div>
                            </article>
                            {accountCreatedAt ? (
                              <p
                                className="personal-info-created"
                                aria-label={t("accountCreatedAt")}
                              >
                                <FaCalendarAlt aria-hidden />
                                <span>
                                  {t("accountCreatedAt")}
                                  {": "}
                                  <strong>
                                    {formatIsoDate(accountCreatedAt, i18n.language)}
                                  </strong>
                                </span>
                              </p>
                            ) : null}
                            </div>

                            <article className="resto-info-card personal-info-card">
                              <div className="resto-info-card__head">
                                <span className="resto-info-card__icon" aria-hidden>
                                  <FaPhone />
                                </span>
                                <div>
                                  <h3>
                                    {t("personalInfoContact", {
                                      defaultValue: "Contact",
                                    })}
                                  </h3>
                                </div>
                              </div>
                              <div className="resto-info-card__body personal-info-fields">
                                <div className="personal-info-field">
                                  <label className="form-label" htmlFor="personal-phone">
                                    {t("phone")}
                                  </label>
                                  <CountryPhoneInput
                                    value={phone}
                                    onChange={(nextPhone) => setPhone(nextPhone)}
                                    inputProps={{
                                      id: "personal-phone",
                                      autoComplete: "tel",
                                    }}
                                  />
                                </div>
                                <div className="personal-info-field">
                                  <label className="form-label" htmlFor="personal-whatsapp">
                                    <span className="personal-info-field__label-row">
                                      <FaWhatsapp aria-hidden />
                                      {t("phoneWhatsapp")}
                                    </span>
                                  </label>
                                  <CountryPhoneInput
                                    value={phoneWhatsapp || ""}
                                    onChange={(nextPhone) => setPhoneWhatsapp(nextPhone)}
                                    inputProps={{
                                      id: "personal-whatsapp",
                                      autoComplete: "tel",
                                    }}
                                  />
                                </div>
                              </div>
                            </article>
                          </div>

                          <div className="personal-info-actions">
                            <button
                              className="btn btn-primary"
                              type="button"
                              onClick={handleUpdateUserData}
                            >
                              {t("submitChanges")}
                            </button>
                          </div>
                        </div>
                      </Tab.Pane>

                      <Tab.Pane
                        id="#brand-information"
                        eventKey="Brand Information"
                      >
                        <div className="resto-info-wrapper">
                          <Tab.Container
                            activeKey={activeRestoSubTab}
                            onSelect={(key) => key && setActiveRestoSubTab(key)}
                          >
                            <Nav as="ul" className="nav nav-tabs resto-info-subtabs">
                              <Nav.Item as="li" className="nav-item">
                                <Nav.Link eventKey="General">
                                  <FaInfoCircle style={{ marginRight: 8 }} />
                                  {t("tabGeneral")}
                                </Nav.Link>
                              </Nav.Item>
                              <Nav.Item as="li" className="nav-item">
                                <Nav.Link eventKey="Schedule">
                                  <FaCalendarAlt style={{ marginRight: 8 }} />
                                  {t("tabScheduleLocation")}
                                </Nav.Link>
                              </Nav.Item>
                              <Nav.Item as="li" className="nav-item">
                                <Nav.Link eventKey="links">
                                  <FaWifi style={{ marginRight: 8 }} />
                                  {t("tabLinks")}
                                </Nav.Link>
                              </Nav.Item>
                              <Nav.Item as="li" className="nav-item">
                                <Nav.Link eventKey="Menu">
                                  <FaLanguage style={{ marginRight: 8 }} />
                                  {t("tabMenuLanguages")}
                                </Nav.Link>
                              </Nav.Item>
                              <Nav.Item as="li" className="nav-item">
                                <Nav.Link eventKey="Storytelling">
                                  <FaBookOpen style={{ marginRight: 8 }} />
                                  {t("storytelling")}
                                </Nav.Link>
                              </Nav.Item>
                              <Nav.Item as="li" className="nav-item">
                                <Nav.Link eventKey="Extra">
                                  <FaCog style={{ marginRight: 8 }} />
                                  {t("setting")}
                                </Nav.Link>
                              </Nav.Item>
                            </Nav>
                            <Tab.Content>
                              <Tab.Pane eventKey="Extra">
                                <div className="resto-info-section app-profile-extra-section">
                                  {restaurantData?.createdAt && (
                                    <div
                                      className="mb-3 d-flex align-items-start gap-3"
                                      style={profileMetaPanelStyle}
                                      role="region"
                                      aria-label={t(
                                        "restaurantProfileCreatedAt"
                                      )}
                                    >
                                      <span
                                        className="text-primary flex-shrink-0"
                                        style={{ lineHeight: 1 }}
                                        aria-hidden
                                      >
                                        <FaStore size={22} />
                                      </span>
                                      <div className="min-w-0 flex-grow-1">
                                        <span
                                          className="text-muted text-uppercase fw-semibold d-block mb-1"
                                          style={{
                                            fontSize: "0.68rem",
                                            letterSpacing: "0.06em",
                                          }}
                                        >
                                          {t("restaurantProfileCreatedAt")}
                                        </span>
                                        <span
                                          className="text-dark fw-semibold d-block"
                                          style={{
                                            fontSize: "1rem",
                                            lineHeight: 1.35,
                                          }}
                                        >
                                          {formatIsoDateTime(
                                            restaurantData.createdAt,
                                            i18n.language
                                          )}
                                        </span>
                                      </div>
                                    </div>
                                  )}

                          <div
                            className="p-3 mb-3 border rounded-3"
                            style={{ borderColor: "#ffe3de", backgroundColor: "#fff" }}
                          >
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <label
                                className="form-label text-primary mb-0"
                                htmlFor="extraTaxOptionsEnabled"
                              >
                                {t("tabTaxBilling")}
                              </label>
                              <div className="form-check form-switch m-0">
                                <input
                                  id="extraTaxOptionsEnabled"
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={showExtraTaxOptions}
                                  onChange={(e) =>
                                    setShowExtraTaxOptions(e.target.checked)
                                  }
                                  style={{
                                    backgroundColor: showExtraTaxOptions
                                      ? "#fa8072"
                                      : undefined,
                                    borderColor: "#fa8072",
                                  }}
                                />
                              </div>
                            </div>
                            {showExtraTaxOptions && (
                              <div className="mt-3">
                                <div className="mb-3">
                                  <label className="form-label text-primary">
                                    {restaurantData?.country?.toLowerCase() ===
                                    "portugais"
                                      ? t("nif")
                                      : t("taxId")}
                                  </label>
                                  <input
                                    type="text"
                                    className="form-control"
                                    value={
                                      restaurantData?.country?.toLowerCase() ===
                                      "portugais"
                                        ? restaurantData?.NIF || ""
                                        : restaurantData?.taxIdentification || ""
                                    }
                                    onChange={(e) => {
                                      if (
                                        restaurantData?.country?.toLowerCase() ===
                                        "portugais"
                                      ) {
                                        setRestaurantData({
                                          ...restaurantData,
                                          NIF: e.target.value,
                                        });
                                      } else {
                                        setRestaurantData({
                                          ...restaurantData,
                                          taxIdentification: e.target.value,
                                        });
                                      }
                                    }}
                                  />
                                </div>
                                <div className="mb-3">
                                  <VisualChoiceGrid
                                    label={t("taxStyle")}
                                    accent={safeMenuColors.buttonColor}
                                    value={restaurantData?.taxStyle || "excluded"}
                                    onChange={(taxStyle) =>
                                      setRestaurantData({
                                        ...restaurantData,
                                        taxStyle,
                                      })
                                    }
                                    options={[
                                      {
                                        value: "excluded",
                                        preview: "taxExcluded",
                                        title: `${t("americainStyle")} (${t("taxExcluded")})`,
                                        hint: t("taxExcludedHint"),
                                      },
                                      {
                                        value: "included",
                                        preview: "taxIncluded",
                                        title: `${t("europeanStyle")} (${t("taxIncluded")})`,
                                        hint: t("taxIncludedHint"),
                                      },
                                    ]}
                                  />
                                </div>
                                <div className="mb-3">
                                  <label className="form-label text-primary">
                                    {t("taxPercentage")}
                                  </label>
                                  {(restaurantData?.percentages?.length > 0
                                    ? restaurantData.percentages
                                    : [{ name: "", value: 0 }]
                                  ).map((p, index) => (
                                    <div
                                      className="d-flex align-items-center mb-2"
                                      key={index}
                                    >
                                      <input
                                        type="text"
                                        className="form-control me-2"
                                        placeholder={t("percentageName")}
                                        value={p.name}
                                        onChange={(e) => {
                                          const newPercentages =
                                            restaurantData.percentages?.length > 0
                                              ? [...restaurantData.percentages]
                                              : [{ name: "", value: 0 }];
                                          newPercentages[index].name =
                                            e.target.value;
                                          setRestaurantData({
                                            ...restaurantData,
                                            percentages: newPercentages,
                                          });
                                        }}
                                      />
                                      <input
                                        type="number"
                                        className="form-control me-2"
                                        placeholder="%"
                                        value={p.value}
                                        min={0}
                                        max={100}
                                        onChange={(e) => {
                                          const newPercentages =
                                            restaurantData.percentages?.length > 0
                                              ? [...restaurantData.percentages]
                                              : [{ name: "", value: 0 }];
                                          newPercentages[index].value = Number(
                                            e.target.value
                                          );
                                          setRestaurantData({
                                            ...restaurantData,
                                            percentages: newPercentages,
                                          });
                                        }}
                                      />
                                      <button
                                        type="button"
                                        className="btn btn-sm me-2"
                                        style={{
                                          color: "#FA8072",
                                          borderColor: "#FA8072",
                                        }}
                                        onClick={() => {
                                          const newPercentages = [
                                            ...(restaurantData.percentages || []),
                                          ];
                                          newPercentages.splice(index, 1);
                                          setRestaurantData({
                                            ...restaurantData,
                                            percentages: newPercentages,
                                          });
                                        }}
                                      >
                                        <FaTrash />
                                      </button>
                                    </div>
                                  ))}
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary mt-1"
                                    onClick={() =>
                                      setRestaurantData({
                                        ...restaurantData,
                                        percentages: [
                                          ...(restaurantData.percentages || []),
                                          { name: "", value: 0 },
                                        ],
                                      })
                                    }
                                  >
                                    {t("addPercentage")}
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>

                          <div className="mb-3 extra-digital-menu-card">
                            <div className="extra-option-card-head">
                              <div>
                                <label className="form-label text-primary mb-1">
                                  {t("clientMenuDisplay")}
                                </label>
                                <p className="extra-option-card-subtitle mb-0">
                                  {t("clientMenuDisplayHint")}
                                </p>
                              </div>
                              <span className="extra-option-status-pill">
                                ON
                              </span>
                            </div>

                            <div className="sva-live">
                            <div
                              className="extra-toggle-list"
                              onPointerDown={(event) => {
                                const row = event.target.closest("[data-preview-page]");
                                const nextPage = row?.getAttribute("data-preview-page");
                                if (nextPage === "welcome" || nextPage === "menu") {
                                  setLivePreviewPage(nextPage);
                                }
                              }}
                            >
                              <div className="extra-toggle-row" data-preview-page="menu">
                                <div className="extra-toggle-copy">
                                  <label
                                    className="extra-toggle-title"
                                    htmlFor="clientMenuShowLogo"
                                  >
                                    {t("clientMenuShowLogo")}
                                  </label>
                                </div>
                                <ToggleVisualAid
                                  kind="logo"
                                  on={restaurantData?.clientMenuShowLogo !== false}
                                  accent={safeMenuColors.buttonColor}
                                />
                                <div className="form-check form-switch extra-toggle-switch">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="clientMenuShowLogo"
                                    checked={restaurantData?.clientMenuShowLogo !== false}
                                    onChange={(e) =>
                                      setRestaurantData({
                                        ...restaurantData,
                                        clientMenuShowLogo: e.target.checked,
                                      })
                                    }
                                  />
                                </div>
                              </div>

                              <div className="extra-toggle-row" data-preview-page="welcome">
                                <div className="extra-toggle-copy">
                                  <label
                                    className="extra-toggle-title"
                                    htmlFor="clientWelcomeScreenEnabled"
                                  >
                                    {t("clientWelcomeScreenEnabled")}
                                  </label>
                                  <p className="extra-toggle-hint mb-0">
                                    {t("visualAidWelcomePageHint")}
                                  </p>
                                </div>
                                <ToggleVisualAid
                                  kind="welcome"
                                  on={restaurantData?.clientWelcomeScreenEnabled !== false}
                                  accent={safeMenuColors.buttonColor}
                                />
                                <div className="form-check form-switch extra-toggle-switch">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="clientWelcomeScreenEnabled"
                                    checked={
                                      restaurantData?.clientWelcomeScreenEnabled !== false
                                    }
                                    onChange={(e) =>
                                      setRestaurantData({
                                        ...restaurantData,
                                        clientWelcomeScreenEnabled: e.target.checked,
                                      })
                                    }
                                  />
                                </div>
                              </div>

                              <div className="extra-toggle-row" data-preview-page="welcome">
                                <div className="extra-toggle-copy">
                                  <label
                                    className="extra-toggle-title"
                                    htmlFor="clientWelcomeVideoEnabled"
                                  >
                                    {t("clientWelcomeVideoEnabled")}
                                  </label>
                                  <p className="extra-toggle-hint mb-0">
                                    {t("visualAidVideoFirstPageHint")}
                                  </p>
                                </div>
                                <ToggleVisualAid
                                  kind="video"
                                  on={restaurantData?.clientWelcomeVideoEnabled !== false}
                                  accent={safeMenuColors.buttonColor}
                                />
                                <div className="form-check form-switch extra-toggle-switch">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="clientWelcomeVideoEnabled"
                                    checked={
                                      restaurantData?.clientWelcomeVideoEnabled !== false
                                    }
                                    onChange={(e) =>
                                      setRestaurantData({
                                        ...restaurantData,
                                        clientWelcomeVideoEnabled: e.target.checked,
                                      })
                                    }
                                  />
                                </div>
                              </div>

                              <div className="extra-toggle-row" data-preview-page="menu">
                                <div className="extra-toggle-copy">
                                  <label
                                    className="extra-toggle-title"
                                    htmlFor="clientShowAllMenusOnOnePage"
                                  >
                                    {t("clientShowAllMenusOnOnePage")}
                                  </label>
                                </div>
                                <ToggleVisualAid
                                  kind="onePage"
                                  on={restaurantData?.clientShowAllMenusOnOnePage === true}
                                  accent={safeMenuColors.buttonColor}
                                />
                                <div className="form-check form-switch extra-toggle-switch">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="clientShowAllMenusOnOnePage"
                                    checked={
                                      restaurantData?.clientShowAllMenusOnOnePage === true
                                    }
                                    onChange={(e) =>
                                      setRestaurantData({
                                        ...restaurantData,
                                        clientShowAllMenusOnOnePage: e.target.checked,
                                      })
                                    }
                                  />
                                </div>
                              </div>

                              <div className="extra-toggle-row" data-preview-page="menu">
                                <div className="extra-toggle-copy">
                                  <label
                                    className="extra-toggle-title"
                                    htmlFor="clientShowClaimButton"
                                  >
                                    {t("clientShowClaimButton")}
                                  </label>
                                </div>
                                <ToggleVisualAid
                                  kind="claim"
                                  on={restaurantData?.clientShowClaimButton !== false}
                                  accent={safeMenuColors.buttonColor}
                                />
                                <div className="form-check form-switch extra-toggle-switch">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="clientShowClaimButton"
                                    checked={restaurantData?.clientShowClaimButton !== false}
                                    onChange={(e) =>
                                      setRestaurantData({
                                        ...restaurantData,
                                        clientShowClaimButton: e.target.checked,
                                      })
                                    }
                                  />
                                </div>
                              </div>

                              <div className="extra-toggle-row" data-preview-page="menu">
                                <div className="extra-toggle-copy">
                                  <label
                                    className="extra-toggle-title"
                                    htmlFor="clientMenuVoiceEnabled"
                                  >
                                    {t("clientMenuVoiceEnabled")}
                                  </label>
                                  <p className="extra-toggle-hint mb-0">
                                    {t("clientMenuVoiceEnabledHint")}
                                  </p>
                                </div>
                                <ToggleVisualAid
                                  kind="voice"
                                  on={restaurantData?.clientMenuVoiceEnabled === true}
                                  accent={safeMenuColors.buttonColor}
                                />
                                <div className="form-check form-switch extra-toggle-switch">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="clientMenuVoiceEnabled"
                                    checked={restaurantData?.clientMenuVoiceEnabled === true}
                                    onChange={(e) =>
                                      setRestaurantData({
                                        ...restaurantData,
                                        clientMenuVoiceEnabled: e.target.checked,
                                      })
                                    }
                                  />
                                </div>
                              </div>

                              {restaurantData?.clientMenuVoiceEnabled === true ? (
                                <>
                                  <div className="extra-toggle-row" data-preview-page="menu">
                                    <div className="extra-toggle-copy">
                                      <label
                                        className="extra-toggle-title"
                                        htmlFor="clientMenuVoiceMode"
                                      >
                                        {t("clientMenuVoiceMode")}
                                      </label>
                                    </div>
                                    <select
                                      id="clientMenuVoiceMode"
                                      className="form-select form-select-sm"
                                      style={{ maxWidth: 220 }}
                                      value={
                                        restaurantData?.clientMenuVoiceMode === "auto_product"
                                          ? "auto_product"
                                          : "on_click"
                                      }
                                      onChange={(e) =>
                                        setRestaurantData({
                                          ...restaurantData,
                                          clientMenuVoiceMode: e.target.value,
                                        })
                                      }
                                    >
                                      <option value="on_click">
                                        {t("clientMenuVoiceModeOnClick")}
                                      </option>
                                      <option value="auto_product">
                                        {t("clientMenuVoiceModeAuto")}
                                      </option>
                                    </select>
                                  </div>

                                  <div className="extra-toggle-row" data-preview-page="menu">
                                    <div className="extra-toggle-copy">
                                      <label
                                        className="extra-toggle-title"
                                        htmlFor="clientMenuVoiceIncludeDescription"
                                      >
                                        {t("clientMenuVoiceIncludeDescription")}
                                      </label>
                                    </div>
                                    <ToggleVisualAid
                                      kind="voice"
                                      on={
                                        restaurantData?.clientMenuVoiceIncludeDescription ===
                                        true
                                      }
                                      accent={safeMenuColors.buttonColor}
                                    />
                                    <div className="form-check form-switch extra-toggle-switch">
                                      <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="clientMenuVoiceIncludeDescription"
                                        checked={
                                          restaurantData?.clientMenuVoiceIncludeDescription ===
                                          true
                                        }
                                        onChange={(e) =>
                                          setRestaurantData({
                                            ...restaurantData,
                                            clientMenuVoiceIncludeDescription:
                                              e.target.checked,
                                          })
                                        }
                                      />
                                    </div>
                                  </div>
                                </>
                              ) : null}

                              <div className="extra-toggle-row" data-preview-page="welcome">
                                <div className="extra-toggle-copy">
                                  <label
                                    className="extra-toggle-title"
                                    htmlFor="clientShowWelcomeContactButton"
                                  >
                                    {t(
                                      "clientShowWelcomeContactButton",
                                      "Show Contact button on the welcome page"
                                    )}
                                  </label>
                                </div>
                                <ToggleVisualAid
                                  kind="contact"
                                  on={restaurantData?.clientShowWelcomeContactButton !== false}
                                  accent={safeMenuColors.buttonColor}
                                />
                                <div className="form-check form-switch extra-toggle-switch">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="clientShowWelcomeContactButton"
                                    checked={
                                      restaurantData?.clientShowWelcomeContactButton !== false
                                    }
                                    onChange={(e) =>
                                      setRestaurantData({
                                        ...restaurantData,
                                        clientShowWelcomeContactButton:
                                          e.target.checked,
                                      })
                                    }
                                  />
                                </div>
                              </div>

                              <div className="extra-toggle-row" data-preview-page="welcome">
                                <div className="extra-toggle-copy">
                                  <label
                                    className="extra-toggle-title"
                                    htmlFor="clientShowStorytellingButton"
                                  >
                                    {t("clientShowStorytellingButton")}
                                  </label>
                                </div>
                                <ToggleVisualAid
                                  kind="story"
                                  on={restaurantData?.clientShowStorytellingButton !== false}
                                  accent={safeMenuColors.buttonColor}
                                />
                                <div className="form-check form-switch extra-toggle-switch">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="clientShowStorytellingButton"
                                    checked={
                                      restaurantData?.clientShowStorytellingButton !== false
                                    }
                                    onChange={(e) =>
                                      setRestaurantData({
                                        ...restaurantData,
                                        clientShowStorytellingButton: e.target.checked,
                                      })
                                    }
                                  />
                                </div>
                              </div>

                              <div className="extra-toggle-row" data-preview-page="welcome">
                                <div className="extra-toggle-copy">
                                  <label
                                    className="extra-toggle-title"
                                    htmlFor="clientShowEventsButton"
                                  >
                                    {t("clientShowEventsButton")}
                                  </label>
                                </div>
                                <ToggleVisualAid
                                  kind="events"
                                  on={restaurantData?.clientShowEventsButton !== false}
                                  accent={safeMenuColors.buttonColor}
                                />
                                <div className="form-check form-switch extra-toggle-switch">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="clientShowEventsButton"
                                    checked={
                                      restaurantData?.clientShowEventsButton !== false
                                    }
                                    onChange={(e) =>
                                      setRestaurantData({
                                        ...restaurantData,
                                        clientShowEventsButton: e.target.checked,
                                      })
                                    }
                                  />
                                </div>
                              </div>

                              <div className="extra-toggle-row" data-preview-page="welcome">
                                <div className="extra-toggle-copy">
                                  <label
                                    className="extra-toggle-title"
                                    htmlFor="clientShowReservationButton"
                                  >
                                    {t("clientShowReservationButton")}
                                  </label>
                                </div>
                                <ToggleVisualAid
                                  kind="book"
                                  on={restaurantData?.clientShowReservationButton !== false}
                                  accent={safeMenuColors.buttonColor}
                                />
                                <div className="form-check form-switch extra-toggle-switch">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="clientShowReservationButton"
                                    checked={
                                      restaurantData?.clientShowReservationButton !== false
                                    }
                                    onChange={(e) =>
                                      setRestaurantData({
                                        ...restaurantData,
                                        clientShowReservationButton: e.target.checked,
                                      })
                                    }
                                  />
                                </div>
                              </div>
                            </div>
                            <ClientMenuLivePreview
                              restaurantData={restaurantData}
                              accent={safeMenuColors.buttonColor}
                              focusPage={livePreviewPage}
                            />
                            </div>
                          </div>

                          <div className="mb-3 extra-policy-card">
                            <div className="extra-option-card-head">
                              <div>
                                <label className="form-label text-primary mb-1">
                                  Info tab policies
                                </label>
                                <p className="extra-option-card-subtitle mb-0">
                                  Shown to customers in the Info tab of the digital menu.
                                </p>
                              </div>
                              <span className="extra-option-status-pill">
                                INFO
                              </span>
                            </div>

                            <div className="policy-editor-section">
                              <label className="form-label text-primary">
                                Dress code
                              </label>
                              <textarea
                                className="form-control"
                                rows={2}
                                placeholder="Example: Smart casual. Please avoid beachwear."
                                value={restaurantData?.dressCode?.description || ""}
                                onChange={(e) =>
                                  updateDressCodeField("description", e.target.value)
                                }
                              />
                            </div>

                            {[
                              {
                                key: "requiredItems",
                                title: "Required / accepted",
                                button: "Add accepted item",
                                placeholder: "Example: Smart shoes",
                              },
                              {
                                key: "forbiddenItems",
                                title: "Forbidden",
                                button: "Add forbidden item",
                                placeholder: "Example: Flip-flops",
                              },
                            ].map((group) => (
                              <div className="policy-editor-section" key={group.key}>
                                <div className="policy-editor-head">
                                  <label className="form-label text-primary mb-0">
                                    {group.title}
                                  </label>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => addDressCodeItem(group.key)}
                                  >
                                    {group.button}
                                  </button>
                                </div>

                                {(restaurantData?.dressCode?.[group.key] || []).length === 0 ? (
                                  <p className="text-muted mb-0">
                                    Add one or more icon rows, or leave empty.
                                  </p>
                                ) : (
                                  <div className="policy-item-list">
                                    {(restaurantData?.dressCode?.[group.key] || []).map(
                                      (item, index) => (
                                        <div className="policy-item-row" key={index}>
                                          <select
                                            className="form-select policy-icon-select"
                                            value={item.icon || "✅"}
                                            onChange={(e) =>
                                              updateDressCodeItem(
                                                group.key,
                                                index,
                                                "icon",
                                                e.target.value
                                              )
                                            }
                                          >
                                            {POLICY_ICON_OPTIONS.map((icon) => (
                                              <option key={icon} value={icon}>
                                                {icon}
                                              </option>
                                            ))}
                                          </select>
                                          <input
                                            type="text"
                                            className="form-control"
                                            placeholder={group.placeholder}
                                            value={item.label || ""}
                                            onChange={(e) =>
                                              updateDressCodeItem(
                                                group.key,
                                                index,
                                                "label",
                                                e.target.value
                                              )
                                            }
                                          />
                                          <button
                                            type="button"
                                            className="btn btn-outline-danger"
                                            onClick={() =>
                                              removeDressCodeItem(group.key, index)
                                            }
                                          >
                                            <FaTrash />
                                          </button>
                                        </div>
                                      )
                                    )}
                                  </div>
                                )}
                              </div>
                            ))}

                            <div className="policy-editor-section">
                              <label className="form-label text-primary">
                                Restaurant policies
                              </label>
                              <textarea
                                className="form-control"
                                rows={4}
                                placeholder="Example: Outside food is not allowed. Please inform staff about allergies."
                                value={restaurantData?.restaurantPolicies || ""}
                                onChange={(e) =>
                                  setRestaurantData({
                                    ...restaurantData,
                                    restaurantPolicies: e.target.value,
                                  })
                                }
                              />
                            </div>

                            <div className="policy-editor-section">
                              <label className="form-label text-primary">
                                Refund policies
                              </label>
                              <textarea
                                className="form-control"
                                rows={4}
                                placeholder="Example: Refunds are reviewed by management. Paid orders cannot be refunded after preparation starts."
                                value={restaurantData?.refundPolicies || ""}
                                onChange={(e) =>
                                  setRestaurantData({
                                    ...restaurantData,
                                    refundPolicies: e.target.value,
                                  })
                                }
                              />
                            </div>
                          </div>

                          {/* Menu colors — native color pickers + hex */}
                          <div
                            className="mb-4 p-3 border rounded app-profile-menu-colors"
                            style={{ backgroundColor: "#f9f9f9" }}
                          >
                            <label
                              className="form-label text-primary fw-bold mb-3"
                              style={{ display: "block", fontSize: "1.1rem" }}
                            >
                              {t("menuColors")}
                            </label>

                            <div className="app-profile-menu-colors-grid">
                              <div className="app-profile-menu-color-field">
                                <label
                                  className="form-label mb-2"
                                  style={{ fontWeight: 500 }}
                                  htmlFor="menu-color-button"
                                >
                                  {t("colorBtn")}
                                </label>
                                <div className="app-profile-menu-color-row">
                                  <input
                                    id="menu-color-button"
                                    type="color"
                                    className="app-profile-color-picker"
                                    aria-label={t("colorBtn")}
                                    value={normalizeHexForColorInput(
                                      safeMenuColors.buttonColor
                                    )}
                                    onChange={(e) => {
                                      const v = e.target.value.toUpperCase();
                                      setRestaurantData((prev) => ({
                                        ...prev,
                                        menuColors: {
                                          ...(prev?.menuColors || {
                                            buttonColor: "#f47f72",
                                            tableQRCode: "#f47f72",
                                          }),
                                          buttonColor: v,
                                        },
                                      }));
                                    }}
                                  />
                                  <input
                                    type="text"
                                    value={safeMenuColors.buttonColor}
                                    onChange={(e) => {
                                      const value =
                                        e.target.value.toUpperCase();
                                      if (
                                        /^#([0-9A-F]{0,6})$/i.test(value) ||
                                        value === ""
                                      ) {
                                        setRestaurantData((prev) => ({
                                          ...prev,
                                          menuColors: {
                                            ...safeMenuColors,
                                            buttonColor: value,
                                          },
                                        }));
                                      }
                                    }}
                                    placeholder="#F47F72"
                                    className="app-profile-menu-hex-input"
                                    spellCheck={false}
                                  />
                                  <div
                                    className="app-profile-menu-color-swatch"
                                    style={{
                                      backgroundColor: /^#[0-9A-F]{6}$/i.test(
                                        safeMenuColors.buttonColor
                                      )
                                        ? safeMenuColors.buttonColor
                                        : "transparent",
                                    }}
                                    title={safeMenuColors.buttonColor}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>

                          <div
                            className="mb-3 p-3 border rounded-3"
                            style={{ borderColor: "#ffe3de", backgroundColor: "#fff" }}
                          >
                            <label className="form-label text-primary">
                              {t("welcomeBackground")}
                            </label>
                            <div
                              style={{
                                fontSize: "0.9rem",
                                color: "#6c757d",
                                marginBottom: "10px",
                              }}
                            >
                              {t("welcomeBackgroundInfo")}
                            </div>
                            <div
                              style={{
                                display: "flex",
                                gap: 10,
                                alignItems: "flex-end",
                              }}
                            >
                              <div
                                style={{
                                  width: "220px",
                                  height: "150px",
                                  position: "relative",
                                }}
                              >
                                {previewWelcomeBackgroundVideo ||
                                restaurantData?.welcomeBackgroundVideo ? (
                                  <video
                                    src={
                                      previewWelcomeBackgroundVideo ||
                                      getRestaurantUploadUrl(
                                        "restaurant",
                                        restaurantData.welcomeBackgroundVideo
                                      )
                                    }
                                    muted
                                    playsInline
                                    autoPlay
                                    loop
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                      borderRadius: 10,
                                      border: previewWelcomeBackgroundVideo
                                        ? "2px solid #FA8072"
                                        : "1px solid #ccc",
                                      background: "#000",
                                    }}
                                  />
                                ) : previewWelcomePhoto ||
                                  restaurantData?.welcomePhoto ? (
                                  <img
                                    lazy="loading"
                                    src={
                                      previewWelcomePhoto ||
                                      getRestaurantUploadUrl(
                                        "restaurant",
                                        restaurantData.welcomePhoto
                                      )
                                    }
                                    alt="Welcome background"
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "cover",
                                      borderRadius: 10,
                                      border: previewWelcomePhoto
                                        ? "2px solid #FA8072"
                                        : "1px solid #ccc",
                                    }}
                                  />
                                ) : (
                                  <div
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      borderRadius: 10,
                                      border: "1px dashed #ccc",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      color: "#999",
                                      fontSize: "0.9rem",
                                      padding: "0.75rem",
                                      textAlign: "center",
                                    }}
                                  >
                                    {t("noWelcomeBackground")}
                                  </div>
                                )}
                                <input
                                  id="welcome-photo-upload"
                                  type="file"
                                  accept="image/*"
                                  style={{ display: "none" }}
                                  ref={welcomePhotoInputRef}
                                  onChange={handleWelcomePhotoChange}
                                />
                                <input
                                  id="welcome-background-video-upload"
                                  type="file"
                                  accept="video/*"
                                  style={{ display: "none" }}
                                  ref={welcomeBackgroundVideoInputRef}
                                  onChange={handleWelcomeBackgroundVideoChange}
                                />
                                {backgroundVideoEndSec > backgroundVideoStartSec && (
                                  <p className="mb-0 mt-2" style={{ fontSize: "0.82rem", color: "#6b625b" }}>
                                    {t("videoSliceRange", {
                                      start: formatVideoClock(backgroundVideoStartSec),
                                      end: formatVideoClock(backgroundVideoEndSec),
                                    })}
                                  </p>
                                )}
                              </div>

                              <div
                                className="d-flex flex-column"
                                style={{
                                  gap: 8,
                                  alignItems: "stretch",
                                  justifyContent: "flex-end",
                                  minHeight: "150px",
                                }}
                              >
                                <label
                                  htmlFor="welcome-photo-upload"
                                  className="btn btn-sm btn-outline-primary mb-0"
                                  style={{ cursor: "pointer" }}
                                >
                                  {t("uploadWelcomePhoto")}
                                </label>
                                <label
                                  htmlFor="welcome-background-video-upload"
                                  className="btn btn-sm btn-outline-primary mb-0"
                                  style={{ cursor: "pointer" }}
                                >
                                  {t("uploadWelcomeBackgroundVideo")}
                                </label>

                                {(restaurantData?.welcomePhoto ||
                                  previewWelcomePhoto ||
                                  restaurantData?.welcomeBackgroundVideo ||
                                  previewWelcomeBackgroundVideo) && (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={async () => {
                                      const result = await Swal.fire({
                                        title: t("deleteWelcomeBackground"),
                                        icon: "question",
                                        showCancelButton: true,
                                        confirmButtonText: t("confirm"),
                                        cancelButtonText: t("cancel"),
                                        confirmButtonColor: "#f47f72",
                                        cancelButtonColor: "#87afca",
                                      });
                                      if (!result.isConfirmed) return;
                                      try {
                                        if (restaurantData?.welcomeBackgroundVideo) {
                                          await deleteRestaurantWelcomeBackgroundVideo();
                                        }
                                        if (restaurantData?.welcomePhoto) {
                                          await deleteRestaurantWelcomePhoto();
                                        }
                                        setRestaurantData((prev) => ({
                                          ...prev,
                                          welcomePhoto: null,
                                          welcomeBackgroundVideo: null,
                                        }));
                                        setPreviewWelcomePhoto(null);
                                        setSelectedWelcomePhoto(null);
                                        setPreviewWelcomeBackgroundVideo(null);
                                        setSelectedWelcomeBackgroundVideo(null);
                                        if (welcomePhotoInputRef.current) {
                                          welcomePhotoInputRef.current.value = "";
                                        }
                                        if (welcomeBackgroundVideoInputRef.current) {
                                          welcomeBackgroundVideoInputRef.current.value =
                                            "";
                                        }
                                        toast.success(t("welcomeBackgroundDeleted"));
                                      } catch (error) {
                                        toast.error(
                                          error?.message || t("errorUpdating")
                                        );
                                      }
                                    }}
                                  >
                                    {t("delete") || "Delete"}
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>

                          <div
                            className="mb-3 p-3 border rounded-3"
                            style={{ borderColor: "#ffe3de", backgroundColor: "#fff" }}
                          >
                            <label className="form-label text-primary">
                              {t("welcomeVideo")}
                            </label>
                            <div
                              style={{
                                fontSize: "0.9rem",
                                color: "#6c757d",
                                marginBottom: "10px",
                              }}
                            >
                              {t("welcomeVideoInfo")}
                            </div>
                            <div
                              style={{
                                display: "flex",
                                gap: 10,
                                alignItems: "flex-end",
                              }}
                            >
                              <div
                                style={{
                                  width: "280px",
                                  maxWidth: "100%",
                                  minHeight: "150px",
                                  position: "relative",
                                }}
                              >
                                {previewWelcomeVideo ? (
                                  <video
                                    src={previewWelcomeVideo}
                                    controls
                                    style={{
                                      width: "100%",
                                      borderRadius: 10,
                                      border: "2px solid #FA8072",
                                      background: "#000",
                                    }}
                                  />
                                ) : restaurantData?.welcomeVideo ? (
                                  <video
                                    src={getRestaurantUploadUrl(
                                      "restaurant",
                                      restaurantData.welcomeVideo
                                    )}
                                    controls
                                    style={{
                                      width: "100%",
                                      borderRadius: 10,
                                      border: "1px solid #ccc",
                                      background: "#000",
                                    }}
                                  />
                                ) : (
                                  <div
                                    style={{
                                      width: "100%",
                                      minHeight: "150px",
                                      borderRadius: 10,
                                      border: "1px dashed #ccc",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      color: "#999",
                                      fontSize: "0.9rem",
                                      padding: "1rem",
                                      textAlign: "center",
                                    }}
                                  >
                                    {t("noWelcomeVideo")}
                                  </div>
                                )}
                                <input
                                  id="welcome-video-upload-extra"
                                  type="file"
                                  accept="video/*"
                                  style={{ display: "none" }}
                                  ref={welcomeVideoInputRef}
                                  onChange={handleWelcomeVideoChange}
                                />
                                {welcomeVideoEndSec > welcomeVideoStartSec && (
                                  <p className="mb-0 mt-2" style={{ fontSize: "0.82rem", color: "#6b625b" }}>
                                    {t("videoSliceRange", {
                                      start: formatVideoClock(welcomeVideoStartSec),
                                      end: formatVideoClock(welcomeVideoEndSec),
                                    })}
                                  </p>
                                )}
                              </div>

                              <div
                                className="d-flex flex-column"
                                style={{
                                  gap: 8,
                                  alignItems: "center",
                                  justifyContent: "flex-end",
                                  minHeight: "150px",
                                }}
                              >
                                <label
                                  htmlFor="welcome-video-upload-extra"
                                  style={{
                                    backgroundColor: "#FA8072",
                                    borderRadius: "50%",
                                    padding: 8,
                                    cursor: "pointer",
                                    color: "white",
                                    boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <FaPencilAlt size={15} />
                                </label>

                                {(restaurantData?.welcomeVideo || previewWelcomeVideo) && (
                                  <button
                                    type="button"
                                    onClick={async () => {
                                      const result = await Swal.fire({
                                        title: t("deleteWelcomeVideo"),
                                        icon: "question",
                                        showCancelButton: true,
                                        confirmButtonText: t("confirm"),
                                        cancelButtonText: t("cancel"),
                                      });
                                      if (!result.isConfirmed) return;
                                      try {
                                        await deleteRestaurantWelcomeVideo();
                                        setRestaurantData({
                                          ...restaurantData,
                                          welcomeVideo: null,
                                        });
                                        setPreviewWelcomeVideo(null);
                                        setSelectedWelcomeVideo(null);
                                        if (welcomeVideoInputRef.current) {
                                          welcomeVideoInputRef.current.value = "";
                                        }
                                        toast.success(t("welcomeVideoDeleted"));
                                      } catch (error) {
                                        toast.error(
                                          error?.message || t("errorUpdating")
                                        );
                                      }
                                    }}
                                    style={{
                                      backgroundColor: "#dc3545",
                                      borderRadius: "50%",
                                      padding: 8,
                                      cursor: "pointer",
                                      color: "white",
                                      border: "none",
                                      boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <FaTrash size={15} />
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="mb-3">
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: "2px",
                              }}
                            >
                              <label
                                className="form-label text-primary"
                                style={{ marginBottom: "0px" }}
                              >
                                {t("welcomeMessage")}
                              </label>
                              <span
                                onClick={() =>
                                  setShowOtherLanguages(!showOtherLanguages)
                                }
                                style={{
                                  cursor: "pointer",
                                  color: Colors.primary,
                                  fontSize: "0.9rem",
                                  fontWeight: 400,
                                  textDecoration: "underline",
                                }}
                              >
                                {showOtherLanguages
                                  ? t("hideOtherLanguages")
                                  : t("otherLanguages")}
                                &nbsp;
                                {showOtherLanguages ? "▲" : "▼"}
                              </span>
                            </div>
                            <div
                              style={{
                                fontSize: "0.9rem",
                                color: "#6c757d",
                                marginBottom: "10px",
                              }}
                            >
                              {t("welcomeMessageInfo")}
                            </div>
                            {ALL_CLIENT_LANG_CODES.filter(
                              (lang) => showOtherLanguages || lang === "en"
                            ).map((lang) => {
                              const messageObj =
                                restaurantData?.msgBienvenue?.find(
                                  (m) => m.language === lang
                                ) || {
                                  language: lang,
                                  msg: "",
                                };
                              return (
                                <div
                                  key={lang}
                                  className="d-flex align-items-center"
                                  style={{ gap: "10px", marginBottom: "10px" }}
                                >
                                  <span
                                    className="d-flex align-items-center client-lang-label-with-flag"
                                    style={{
                                      width: "88px",
                                      flexShrink: 0,
                                      fontWeight: 500,
                                    }}
                                  >
                                    {getMenuLanguageFlag(lang) ? (
                                      <img
                                        className="client-lang-flag"
                                        src={getMenuLanguageFlag(lang)}
                                        alt=""
                                        width={24}
                                        height={18}
                                      />
                                    ) : null}
                                    {lang.toUpperCase()}
                                  </span>
                                  <input
                                    type="text"
                                    className="form-control"
                                    placeholder={t("message")}
                                    value={messageObj.msg}
                                    maxLength={100}
                                    onChange={(e) => {
                                      const newMessages = [
                                        ...(restaurantData?.msgBienvenue || []),
                                      ];
                                      const existingIndex =
                                        newMessages.findIndex(
                                          (m) => m.language === lang
                                        );
                                      if (existingIndex !== -1) {
                                        newMessages[existingIndex].msg =
                                          e.target.value;
                                      } else {
                                        newMessages.push({
                                          language: lang,
                                          msg: e.target.value,
                                        });
                                      }
                                      setRestaurantData({
                                        ...restaurantData,
                                        msgBienvenue: newMessages,
                                      });
                                    }}
                                  />
                                  <span
                                    style={{
                                      fontSize: "0.8rem",
                                      color: "#6c757d",
                                    }}
                                  >
                                    {messageObj.msg.length}/100
                                  </span>
                                </div>
                              );
                            })}
                          </div>

                              </div>
                            </Tab.Pane>

                              <Tab.Pane eventKey="General">
                                <div className="resto-info-general">
                                  {!franchiseManagingRestaurant ? (
                                    <JoinFranchiseSection restaurantData={restaurantData} />
                                  ) : null}
                                  <article className="resto-info-card resto-info-card--identity">
                                    <div className="resto-info-card__head">
                                      <span className="resto-info-card__icon" aria-hidden>
                                        <FaStore />
                                      </span>
                                      <div>
                                        <h3>{t("restoInfoIdentity")}</h3>
                                        <p>{t("restoInfoIdentityHelp")}</p>
                                      </div>
                                    </div>
                                    <div className="resto-info-identity">
                                      <div>
                                        <label className="form-label">{t("restaurantLogo")}</label>
                                        <div className="resto-logo">
                                          <div
                                            className={`resto-logo__preview${
                                              previewLogo || restaurantData?.logo ? " has-image" : ""
                                            }`}
                                          >
                                            {previewLogo ? (
                                              <img src={previewLogo} alt="" />
                                            ) : restaurantData?.logo ? (
                                              <img
                                                src={`${config.UPLOADS_SERVER}/logos/${restaurantData.logo}`}
                                                alt=""
                                              />
                                            ) : (
                                              t("noLogo")
                                            )}
                                          </div>
                                          <div className="resto-logo__actions">
                                            <input
                                              id="logo-upload"
                                              type="file"
                                              accept="image/*"
                                              onChange={handleLogoChange}
                                              style={{ display: "none" }}
                                              ref={logoInputRef}
                                            />
                                            <label
                                              htmlFor="logo-upload"
                                              className="resto-logo__btn resto-logo__btn--edit"
                                              title={t("restoLogoChange")}
                                            >
                                              <FaPencilAlt size={14} />
                                            </label>
                                            {(restaurantData?.logo || previewLogo) && (
                                              <button
                                                type="button"
                                                className="resto-logo__btn resto-logo__btn--delete"
                                                title={t("deleteLogo")}
                                                onClick={async () => {
                                                  Swal.fire({
                                                    title: t("deleteLogo"),
                                                    icon: "question",
                                                    showCancelButton: true,
                                                    confirmButtonText: t("confirm"),
                                                    cancelButtonText: t("cancel"),
                                                    confirmButtonColor: "#f47f72",
                                                    cancelButtonColor: "#87afca",
                                                  }).then(async (result) => {
                                                    if (result.isConfirmed) {
                                                      try {
                                                        await deleteRestaurantLogo();
                                                        setRestaurantData((prev) => ({
                                                          ...prev,
                                                          logo: null,
                                                        }));
                                                        setPreviewLogo(null);
                                                        setSelectedLogo(null);
                                                        if (logoInputRef.current)
                                                          logoInputRef.current.value = null;
                                                      } catch (err) {
                                                        toast.error(
                                                          err.message || t("errorTitle")
                                                        );
                                                      }
                                                    }
                                                  });
                                                }}
                                              >
                                                <FaTrash size={13} />
                                              </button>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                      <div>
                                        <label className="form-label" htmlFor="resto-name">
                                          {t("restoName")}
                                        </label>
                                        <input
                                          id="resto-name"
                                          type="text"
                                          className="form-control"
                                          value={restaurantData?.nameRes || ""}
                                          onChange={(e) =>
                                            setRestaurantData({
                                              ...restaurantData,
                                              nameRes: e.target.value,
                                            })
                                          }
                                        />
                                      </div>
                                    </div>
                                  </article>

                                  <div className="resto-info-pair">
                                  <article className="resto-info-card">
                                    <div className="resto-info-card__head">
                                      <span className="resto-info-card__icon" aria-hidden>
                                        <FaGlobe />
                                      </span>
                                      <div>
                                        <h3>{t("restoInfoLocation")}</h3>
                                        <p>{t("restoInfoLocationHelp")}</p>
                                      </div>
                                    </div>
                                    <div className="resto-info-card__body">
                                    <label className="form-label" htmlFor="resto-country">
                                      {t("country")}
                                    </label>
                                    <input
                                      id="resto-country"
                                      type="text"
                                      className="form-control"
                                      value={restaurantData?.country || ""}
                                      disabled
                                    />
                                    <label className="form-label mt-3" htmlFor="resto-full-address">
                                      {t("writtenFullAddress", { defaultValue: "Full address" })}
                                    </label>
                                    <input
                                      id="resto-full-address"
                                      type="text"
                                      className="form-control"
                                      autoComplete="street-address"
                                      value={restaurantData?.fullAddress || ""}
                                      onChange={(e) =>
                                        setRestaurantData({
                                          ...restaurantData,
                                          fullAddress: e.target.value,
                                        })
                                      }
                                    />
                                    <p className="link-toggle-row__hint">
                                      {t("writtenFullAddressHint", {
                                        defaultValue:
                                          "Street address as text. The Google Maps link is set separately under Links.",
                                      })}
                                    </p>
                                    </div>
                                  </article>

                                  <article className="resto-info-card">
                                    <div className="resto-info-card__head">
                                      <span className="resto-info-card__icon" aria-hidden>
                                        <FaExchangeAlt />
                                      </span>
                                      <div>
                                        <h3>{t("restoInfoCurrency")}</h3>
                                        <p>{t("restoInfoCurrencyHelp")}</p>
                                      </div>
                                    </div>
                                    <div className="resto-info-card__body">
                          <div className="mb-0 dual-currency-settings">
                            <div className="dual-currency-settings__col">
                              <label className="form-label">
                                {t("currencyMain")}
                              </label>
                              <CurrencySelect
                                value={restaurantData?.currency || "USD"}
                                excludeCodes={
                                  restaurantData?.secondaryCurrency
                                    ? [restaurantData.secondaryCurrency]
                                    : []
                                }
                                onChange={(code) => {
                                  setRestaurantCurrencyCode(code);
                                  const second = restaurantData?.secondaryCurrency;
                                  setRestaurantData({
                                    ...restaurantData,
                                    currency: code,
                                    secondaryCurrency:
                                      second === code ? "" : second,
                                  });
                                  if (second === code) {
                                    setRestaurantSecondaryCurrencyCode("");
                                  }
                                }}
                              />
                              <small className="text-muted">
                                {t("currencyMsg")}
                              </small>
                            </div>

                            <div className="dual-currency-settings__swap-wrap">
                              <button
                                type="button"
                                className="dual-currency-settings__swap"
                                disabled={!restaurantData?.secondaryCurrency}
                                title={t("currencySwitch")}
                                aria-label={t("currencySwitch")}
                                onClick={() => {
                                  const main = restaurantData?.currency || "USD";
                                  const second = restaurantData?.secondaryCurrency;
                                  if (!second) return;
                                  setRestaurantCurrencyCode(second);
                                  setRestaurantSecondaryCurrencyCode(main);
                                  setRestaurantData({
                                    ...restaurantData,
                                    currency: second,
                                    secondaryCurrency: main,
                                  });
                                }}
                              >
                                <FaExchangeAlt />
                              </button>
                            </div>

                            <div className="dual-currency-settings__col">
                              <label className="form-label">
                                {t("currencySecondary")}
                              </label>
                              <CurrencySelect
                                allowEmpty
                                emptyLabel={t("currencySecondaryNone")}
                                excludeCodes={[restaurantData?.currency || "USD"]}
                                value={restaurantData?.secondaryCurrency || ""}
                                onChange={(code) => {
                                  setRestaurantSecondaryCurrencyCode(code);
                                  setRestaurantData({
                                    ...restaurantData,
                                    secondaryCurrency: code || "",
                                  });
                                }}
                              />
                              <small className="text-muted">
                                {t("currencySecondaryMsg")}
                              </small>
                            </div>
                          </div>
                                    </div>
                                  </article>
                                  </div>

                          {false && (
                            <>
                          <div className="mb-3">
                            <label className="form-label text-primary">
                              {t("menuMode")}
                            </label>

                            <div className="d-flex gap-4 align-items-center">
                              {/* ----- Mode View ----- */}
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="menuMode"
                                  id="viewMode"
                                  value="view"
                                  checked={restaurantData?.menuMode === "view"}
                                  onChange={(e) =>
                                    setRestaurantData({
                                      ...restaurantData,
                                      menuMode: e.target.value,
                                    })
                                  }
                                  style={{
                                    accentColor: "#FA8072",
                                  }}
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor="viewMode"
                                  style={{ marginTop: 5 }}
                                >
                                  {t("viewMode")}
                                </label>
                              </div>

                              {/* ----- Mode Ordering ----- */}
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="menuMode"
                                  id="orderingMode"
                                  value="ordering"
                                  checked={
                                    restaurantData?.menuMode === "ordering"
                                  }
                                  onChange={(e) =>
                                    setRestaurantData({
                                      ...restaurantData,
                                      menuMode: e.target.value,
                                    })
                                  }
                                  style={{
                                    accentColor: "#FA8072",
                                  }}
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor="orderingMode"
                                  style={{ marginTop: 5 }}
                                >
                                  {t("orderingMode")}
                                </label>
                              </div>
                            </div>
                          </div>

                            </>
                          )}
                                </div>
                              </Tab.Pane>

                              <Tab.Pane eventKey="Schedule">
                                <div className="resto-info-general schedule-layout">
                                  <article className="resto-info-card schedule-format-card">
                                    <div className="resto-info-card__head schedule-card-head">
                                      <span className="resto-info-card__icon" aria-hidden>
                                        <FaClock />
                                      </span>
                                      <div>
                                        <h3>{t("timeFormat") || "Time format"}</h3>
                                      </div>
                                      <div
                                        className="schedule-segmented"
                                        role="group"
                                        aria-label={t("timeFormat") || "Time format"}
                                      >
                                        <button
                                          type="button"
                                          className={`schedule-segmented-btn${activeTimeFormat === "24h" ? " is-active" : ""}`}
                                          onClick={() =>
                                            restaurantData &&
                                            setRestaurantData({
                                              ...restaurantData,
                                              timeFormat: "24h",
                                            })
                                          }
                                        >
                                          {t("timeFormat24h") || "24-hour (No AM / PM)"}
                                        </button>
                                        <button
                                          type="button"
                                          className={`schedule-segmented-btn${activeTimeFormat === "12h" ? " is-active" : ""}`}
                                          onClick={() =>
                                            restaurantData &&
                                            setRestaurantData({
                                              ...restaurantData,
                                              timeFormat: "12h",
                                            })
                                          }
                                        >
                                          {t("timeFormat12h") || "12-hour (With AM / PM)"}
                                        </button>
                                      </div>
                                    </div>
                                  </article>

                                  <article className="resto-info-card schedule-week-card" aria-label={t("workingHours")}>
                                    <div className="resto-info-card__head schedule-card-head">
                                      <span className="resto-info-card__icon" aria-hidden>
                                        <FaCalendarAlt />
                                      </span>
                                      <div>
                                        <h3>{t("workingHours")}</h3>
                                        <p>
                                          {t("scheduleDaysOpen", {
                                            count: scheduleOpenDaysCount,
                                          })}
                                        </p>
                                      </div>
                                      <div className="schedule-toolbar-actions">
                                        <button
                                          type="button"
                                          className="schedule-quick-btn"
                                          onClick={handleApplyMondayToAllDays}
                                        >
                                          {t("scheduleApplyMondayToAll")}
                                        </button>
                                        <button
                                          type="button"
                                          className="schedule-quick-btn"
                                          onClick={() => handleSetAllDaysOpenState(true)}
                                        >
                                          {t("scheduleOpenAll")}
                                        </button>
                                        <button
                                          type="button"
                                          className="schedule-quick-btn"
                                          onClick={() => handleSetAllDaysOpenState(false)}
                                        >
                                          {t("scheduleCloseAll")}
                                        </button>
                                      </div>
                                    </div>
                                    <section className="schedule-panel schedule-panel-days">
                                    <div className="schedule-days-grid">
                                      {restaurantData?.workHours?.map((dayObj, index) => {
                                        const shiftsList = getDayShifts(dayObj);
                                        const dayKey = dayObj.day;
                                        const bodyVisible = isScheduleDayBodyVisible(dayKey);
                                        const panelId = `schedule-day-panel-${index}`;
                                        const hoursPreview = formatDayHoursPreview(
                                          dayObj,
                                          activeTimeFormat
                                        );
                                        return (
                                          <div
                                            key={index}
                                            className={`schedule-day-card${
                                              dayObj.isOpen
                                                ? " schedule-day-card--open"
                                                : " schedule-day-card--closed"
                                            }${!bodyVisible ? " schedule-day-card--body-collapsed" : ""}`}
                                          >
                                            <div className="schedule-day-card-head">
                                              <button
                                                type="button"
                                                className="schedule-day-card-toggle"
                                                onClick={() => toggleScheduleDayBody(dayKey)}
                                                aria-expanded={bodyVisible}
                                                aria-controls={panelId}
                                                aria-label={
                                                  bodyVisible
                                                    ? t("scheduleCollapseDayDetails", {
                                                        day: t(dayObj.day),
                                                      })
                                                    : t("scheduleExpandDayDetails", {
                                                        day: t(dayObj.day),
                                                      })
                                                }
                                              >
                                                <div className="schedule-day-card-meta">
                                                  <span className="schedule-day-abbr" aria-hidden>
                                                    {weekdayShortLabel(dayObj.day, i18n.language)}
                                                  </span>
                                                  <div className="schedule-day-card-meta-text">
                                                    <div className="schedule-day-name">{t(dayObj.day)}</div>
                                                    <div className="schedule-day-hours-preview">
                                                      {dayObj.isOpen
                                                        ? hoursPreview
                                                        : t("closed")}
                                                    </div>
                                                  </div>
                                                </div>
                                                <FaChevronDown
                                                  className={`schedule-day-card-chevron${bodyVisible ? " is-expanded" : ""}`}
                                                  aria-hidden
                                                />
                                              </button>
                                              <Form.Check
                                                type="switch"
                                                id={`schedule-open-${index}`}
                                                className="schedule-day-switch"
                                                checked={Boolean(dayObj.isOpen)}
                                                onChange={(e) =>
                                                  handleDayOpenChange(index, e.target.checked)
                                                }
                                                aria-label={`${t(dayObj.day)} — ${dayObj.isOpen ? t("open") : t("closed")}`}
                                              />
                                            </div>

                                            {bodyVisible && dayObj.isOpen && (
                                              <div id={panelId} className="schedule-day-card-body">
                                                <div className="schedule-shift-toolbar">
                                                  <span className="schedule-shift-count-label" id={`shift-count-${index}`}>
                                                    {t("shiftsPerDay")}
                                                  </span>
                                                  <div
                                                    className="schedule-shift-count"
                                                    role="group"
                                                    aria-labelledby={`shift-count-${index}`}
                                                  >
                                                    {[1, 2, 3, 4].map((n) => (
                                                      <button
                                                        key={n}
                                                        type="button"
                                                        className={`schedule-shift-count__btn${
                                                          shiftsList.length === n ? " is-active" : ""
                                                        }`}
                                                        onClick={() =>
                                                          handleShiftCountChange(index, n)
                                                        }
                                                      >
                                                        {n}
                                                      </button>
                                                    ))}
                                                  </div>
                                                </div>

                                                <ul className="schedule-shift-list">
                                                  {shiftsList.map((shiftRow, si) => (
                                                    <li key={si} className="schedule-shift-item">
                                                      <div className="schedule-shift-chip">
                                                        {t("shiftLabel", { number: si + 1 })}
                                                      </div>
                                                      <div className="schedule-shift-times">
                                                        <div className="schedule-time-slot">
                                                          <span className="schedule-time-slot-label">{t("scheduleFrom")}</span>
                                                          <div className="color-time-picker schedule-time-picker-wrap">
                                                            <TimePicker
                                                              value={shiftRow.startTime}
                                                              onChange={(value) =>
                                                                handleShiftTimeChange(
                                                                  index,
                                                                  si,
                                                                  "startTime",
                                                                  value
                                                                )
                                                              }
                                                              format={
                                                                activeTimeFormat === "12h"
                                                                  ? "h:mm a"
                                                                  : "HH:mm"
                                                              }
                                                              dropdownOnly
                                                              minuteStep={5}
                                                            />
                                                          </div>
                                                        </div>
                                                        <span className="schedule-shift-connector" aria-hidden>
                                                          →
                                                        </span>
                                                        <div className="schedule-time-slot">
                                                          <span className="schedule-time-slot-label">{t("scheduleTo")}</span>
                                                          <div className="color-time-picker schedule-time-picker-wrap">
                                                            <TimePicker
                                                              value={shiftRow.endTime}
                                                              onChange={(value) =>
                                                                handleShiftTimeChange(
                                                                  index,
                                                                  si,
                                                                  "endTime",
                                                                  value
                                                                )
                                                              }
                                                              format={
                                                                activeTimeFormat === "12h"
                                                                  ? "h:mm a"
                                                                  : "HH:mm"
                                                              }
                                                              dropdownOnly
                                                              minuteStep={5}
                                                            />
                                                          </div>
                                                        </div>
                                                      </div>
                                                    </li>
                                                  ))}
                                                </ul>
                                              </div>
                                            )}
                                            {bodyVisible && !dayObj.isOpen && (
                                              <div id={panelId} className="schedule-day-card-body schedule-day-card-body-muted">
                                                <p className="mb-0 small text-muted">
                                                  {t("scheduleClosedDayExpandedHint") ||
                                                    "Turn the switch on to open this day and set hours."}
                                                </p>
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </section>
                                  </article>
                                </div>
                              </Tab.Pane>
                              <Tab.Pane eventKey="links">
                                <div className="resto-info-general resto-info-links">
                                  <article className="resto-info-card">
                                    <div className="resto-info-card__head">
                                      <span className="resto-info-card__icon" aria-hidden>
                                        <FaLink />
                                      </span>
                                      <div>
                                        <h3>{t("linksSectionGeneral")}</h3>
                                      </div>
                                    </div>
                                    <div className="link-toggle-list">
                                      <LinkToggleRow
                                        id="link-address"
                                        label={t("googleMapsLink", { defaultValue: "Google Maps link" })}
                                        checked={linksVisibility.address}
                                        onCheckedChange={(on) => {
                                          setLinksVisibility((prev) => ({ ...prev, address: on }));
                                          if (!on) {
                                            setRestaurantData({ ...restaurantData, address: "" });
                                          }
                                        }}
                                      >
                                        <input
                                          type="text"
                                          className="form-control"
                                          value={restaurantData?.address || ""}
                                          onChange={(e) =>
                                            setRestaurantData({
                                              ...restaurantData,
                                              address: e.target.value,
                                            })
                                          }
                                        />
                                        <p className="link-toggle-row__hint">{t("fullAddressMsg")}</p>
                                      </LinkToggleRow>
                                      <LinkToggleRow
                                        id="link-email"
                                        label={t("contactEmail")}
                                        checked={linksVisibility.contactEmail}
                                        onCheckedChange={(on) => {
                                          setLinksVisibility((prev) => ({
                                            ...prev,
                                            contactEmail: on,
                                          }));
                                          if (!on) {
                                            setRestaurantData({
                                              ...restaurantData,
                                              contactEmail: null,
                                            });
                                          }
                                        }}
                                      >
                                        <input
                                          type="email"
                                          className="form-control"
                                          placeholder="contact@restaurant.com"
                                          value={restaurantData?.contactEmail || ""}
                                          onChange={(e) =>
                                            setRestaurantData({
                                              ...restaurantData,
                                              contactEmail: e.target.value,
                                            })
                                          }
                                        />
                                        <p className="link-toggle-row__hint">
                                          {t("contactEmailLinksHint")}
                                        </p>
                                      </LinkToggleRow>
                                      <LinkToggleRow
                                        id="link-google-review"
                                        label={t("googleReviewLink")}
                                        checked={linksVisibility.googleReviewLink}
                                        onCheckedChange={(on) => {
                                          setLinksVisibility((prev) => ({
                                            ...prev,
                                            googleReviewLink: on,
                                          }));
                                          if (!on) {
                                            setRestaurantData({
                                              ...restaurantData,
                                              googleReviewLink: null,
                                            });
                                          }
                                        }}
                                      >
                                        <input
                                          type="text"
                                          className="form-control"
                                          value={restaurantData?.googleReviewLink || ""}
                                          onChange={(e) =>
                                            setRestaurantData({
                                              ...restaurantData,
                                              googleReviewLink: e.target.value,
                                            })
                                          }
                                        />
                                        <p className="link-toggle-row__hint">
                                          {t("googleReviewLinkMsg")}
                                        </p>
                                      </LinkToggleRow>
                                    </div>
                                  </article>

                                  <article className="resto-info-card">
                                    <div className="resto-info-card__head">
                                      <span className="resto-info-card__icon" aria-hidden>
                                        <FaShareAlt />
                                      </span>
                                      <div>
                                        <h3>{t("linksSectionSocial")}</h3>
                                      </div>
                                    </div>
                                    <div className="link-toggle-list">
                                      <LinkToggleRow
                                        id="link-instagram"
                                        label={t("instagram")}
                                        checked={linksVisibility.instagramLink}
                                        onCheckedChange={(on) => {
                                          setLinksVisibility((prev) => ({
                                            ...prev,
                                            instagramLink: on,
                                          }));
                                          if (!on) {
                                            setRestaurantData({
                                              ...restaurantData,
                                              instagramLink: null,
                                            });
                                          }
                                        }}
                                      >
                                        <input
                                          type="url"
                                          className="form-control"
                                          placeholder="https://instagram.com/..."
                                          value={restaurantData?.instagramLink || ""}
                                          onChange={(e) =>
                                            setRestaurantData({
                                              ...restaurantData,
                                              instagramLink: e.target.value || null,
                                            })
                                          }
                                        />
                                      </LinkToggleRow>
                                      <LinkToggleRow
                                        id="link-facebook"
                                        label={t("facebook")}
                                        checked={linksVisibility.facebookLink}
                                        onCheckedChange={(on) => {
                                          setLinksVisibility((prev) => ({
                                            ...prev,
                                            facebookLink: on,
                                          }));
                                          if (!on) {
                                            setRestaurantData({
                                              ...restaurantData,
                                              facebookLink: null,
                                            });
                                          }
                                        }}
                                      >
                                        <input
                                          type="url"
                                          className="form-control"
                                          placeholder="https://facebook.com/..."
                                          value={restaurantData?.facebookLink || ""}
                                          onChange={(e) =>
                                            setRestaurantData({
                                              ...restaurantData,
                                              facebookLink: e.target.value || null,
                                            })
                                          }
                                        />
                                      </LinkToggleRow>
                                      <LinkToggleRow
                                        id="link-tiktok"
                                        label={t("tiktok")}
                                        checked={linksVisibility.tiktokLink}
                                        onCheckedChange={(on) => {
                                          setLinksVisibility((prev) => ({
                                            ...prev,
                                            tiktokLink: on,
                                          }));
                                          if (!on) {
                                            setRestaurantData({
                                              ...restaurantData,
                                              tiktokLink: null,
                                            });
                                          }
                                        }}
                                      >
                                        <input
                                          type="url"
                                          className="form-control"
                                          placeholder="https://tiktok.com/@..."
                                          value={restaurantData?.tiktokLink || ""}
                                          onChange={(e) =>
                                            setRestaurantData({
                                              ...restaurantData,
                                              tiktokLink: e.target.value || null,
                                            })
                                          }
                                        />
                                      </LinkToggleRow>
                                      <LinkToggleRow
                                        id="link-twitter"
                                        label="X (Twitter)"
                                        checked={linksVisibility.twitterLink}
                                        onCheckedChange={(on) => {
                                          setLinksVisibility((prev) => ({
                                            ...prev,
                                            twitterLink: on,
                                          }));
                                          if (!on) {
                                            setRestaurantData({
                                              ...restaurantData,
                                              twitterLink: null,
                                            });
                                          }
                                        }}
                                      >
                                        <input
                                          type="url"
                                          className="form-control"
                                          placeholder="https://x.com/..."
                                          value={restaurantData?.twitterLink || ""}
                                          onChange={(e) =>
                                            setRestaurantData({
                                              ...restaurantData,
                                              twitterLink: e.target.value || null,
                                            })
                                          }
                                        />
                                      </LinkToggleRow>
                                    </div>
                                  </article>

                                  <article className="resto-info-card resto-info-card--wifi">
                                    <div className="resto-info-card__head">
                                      <span className="resto-info-card__icon" aria-hidden>
                                        <FaWifi />
                                      </span>
                                      <div>
                                        <h3>{t("wifiSectionTitle")}</h3>
                                      </div>
                                    </div>
                                    <div className="wifi-fields">
                                      <div className="wifi-fields__qr">
                                        <span className="form-label">{t("wifiQR")}</span>
                                        <div className="resto-logo">
                                          <div
                                            className={`resto-logo__preview${
                                              previewWifiQR || restaurantData?.wifiQR
                                                ? " has-image"
                                                : ""
                                            }`}
                                          >
                                            {previewWifiQR ? (
                                              <img src={previewWifiQR} alt="" />
                                            ) : restaurantData?.wifiQR ? (
                                              <img
                                                src={`${config.UPLOADS_SERVER}/restaurant/${restaurantData.wifiQR}`}
                                                alt=""
                                              />
                                            ) : (
                                              t("noImage")
                                            )}
                                          </div>
                                          <div className="resto-logo__actions">
                                            <input
                                              id="wifiQR-upload"
                                              type="file"
                                              accept="image/*"
                                              style={{ display: "none" }}
                                              onChange={(e) => {
                                                const file = e.target.files?.[0];
                                                if (file) {
                                                  setPreviewWifiQR(URL.createObjectURL(file));
                                                  setSelectedWifiQR(file);
                                                }
                                              }}
                                            />
                                            <label
                                              htmlFor="wifiQR-upload"
                                              className="resto-logo__btn resto-logo__btn--edit"
                                            >
                                              <FaPencilAlt size={14} />
                                            </label>
                                          </div>
                                        </div>
                                      </div>
                                      <label className="wifi-fields__field">
                                        <span className="form-label">{t("wifiUser")}</span>
                                        <input
                                          type="text"
                                          className="form-control"
                                          value={restaurantData?.wifiUser || ""}
                                          onChange={(e) =>
                                            setRestaurantData({
                                              ...restaurantData,
                                              wifiUser: e.target.value,
                                            })
                                          }
                                        />
                                      </label>
                                      <label className="wifi-fields__field">
                                        <span className="form-label">{t("wifiPwd")}</span>
                                        <input
                                          className="form-control"
                                          value={restaurantData?.wifiPwd || ""}
                                          onChange={(e) =>
                                            setRestaurantData({
                                              ...restaurantData,
                                              wifiPwd: e.target.value,
                                            })
                                          }
                                        />
                                      </label>
                                    </div>

                                  {false && (
                                    <div className="p-3 mb-3 border rounded-3" style={{ borderColor: "#ffe3de", backgroundColor: "#fff" }}>
                                    <label className="form-label text-primary">
                                      {t("serviceStyle")}
                                    </label>
                                    <div className="d-flex gap-4 align-items-center flex-wrap">
                                      <div className="form-check">
                                        <input
                                          className="form-check-input"
                                          type="radio"
                                          name="serviceType"
                                          id="selfService"
                                          value="Self Service"
                                          checked={
                                            restaurantData?.serviceType?.toLowerCase() ===
                                            "self service"
                                          }
                                          onChange={(e) =>
                                            setRestaurantData({
                                              ...restaurantData,
                                              serviceType: e.target.value,
                                            })
                                          }
                                          style={{
                                            accentColor: "#FA8072",
                                          }}
                                        />
                                        <label
                                          className="form-check-label"
                                          htmlFor="selfService"
                                          style={{ marginTop: 5 }}
                                        >
                                          {t("selfService")}
                                        </label>
                                      </div>

                                      <div className="form-check">
                                        <input
                                          className="form-check-input"
                                          type="radio"
                                          name="serviceType"
                                          id="tableService"
                                          value="Table Service"
                                          checked={
                                            restaurantData?.serviceType?.toLowerCase() ===
                                            "table service"
                                          }
                                          onChange={(e) =>
                                            setRestaurantData({
                                              ...restaurantData,
                                              serviceType: e.target.value,
                                            })
                                          }
                                          style={{
                                            accentColor: "#FA8072",
                                          }}
                                        />
                                        <label
                                          className="form-check-label"
                                          htmlFor="tableService"
                                          style={{ marginTop: 5 }}
                                        >
                                          {t("tableService")}
                                        </label>
                                      </div>
                                    </div>
                                  </div>
                                  )}
                                  </article>
                                </div>
                              </Tab.Pane>

                              <Tab.Pane eventKey="Menu">
                                <div className="resto-info-general resto-info-languages">
                                <article className="resto-info-card resto-info-card--languages">
                          {(() => {
                              const enabled = getClientLanguagesEnabled(
                                restaurantData
                              );
                              const disabledCodes = ALL_CLIENT_LANG_CODES.filter(
                                (c) => !enabled.includes(c)
                              );
                              const langLabel = (code) => {
                                const map = {
                                  en: t("english"),
                                  fr: t("french"),
                                  de: t("german"),
                                  es: t("spanish"),
                                  it: t("italian"),
                                  pt: t("portugues"),
                                  ar: t("arabic"),
                                  zh: "中文",
                                  ja: t("japanese"),
                                  ru: t("russian"),
                                };
                                return map[code] || code;
                              };
                              const turnOffLanguage = (code) => {
                                if (enabled.length <= 1) {
                                  toast.error(t("atLeastOneLanguage"));
                                  return;
                                }
                                setRestaurantData({
                                  ...restaurantData,
                                  enabledLanguages: enabled.filter(
                                    (c) => c !== code
                                  ),
                                });
                              };
                              const turnOnLanguage = (code) => {
                                setRestaurantData({
                                  ...restaurantData,
                                  enabledLanguages: [...enabled, code],
                                });
                              };
                              return (
                                <>
                          <div className="resto-info-card__head lang-card-head">
                            <span className="resto-info-card__icon" aria-hidden>
                              <FaLanguage />
                            </span>
                            <div>
                              <h3>{t("clientLanguages")}</h3>
                              <p>{t("clientLanguagesInfo")}</p>
                            </div>
                            {disabledCodes.length > 0 ? (
                              <button
                                type="button"
                                className="lang-activate-btn"
                                onClick={() =>
                                  setRestaurantData({
                                    ...restaurantData,
                                    enabledLanguages: [...ALL_CLIENT_LANG_CODES],
                                  })
                                }
                              >
                                {t("activateAllLanguages")}
                              </button>
                            ) : null}
                          </div>
                          <div className="lang-lists">
                            <section className="lang-list" aria-label={t("languagesActive")}>
                              <div className="lang-list__head">
                                <h4>{t("languagesActive")}</h4>
                                <span className="lang-list__count">{enabled.length}</span>
                              </div>
                              <p className="lang-list__hint">
                                {t("clientLanguagesOrderHint")}
                              </p>
                              <ClientLanguagesSortableList
                                items={enabled}
                                getLabel={langLabel}
                                getFlag={getMenuLanguageFlag}
                                onReorder={(next) =>
                                  setRestaurantData({
                                    ...restaurantData,
                                    enabledLanguages: next,
                                  })
                                }
                                onTurnOff={turnOffLanguage}
                                dragHandleAriaLabel={t("dragLanguageReorder")}
                              />
                            </section>
                            <section className="lang-list" aria-label={t("languagesAvailable")}>
                              <div className="lang-list__head">
                                <h4>{t("languagesAvailable")}</h4>
                                <span className="lang-list__count">{disabledCodes.length}</span>
                              </div>
                              <p className="lang-list__hint">
                                {t("languagesAvailableHint", {
                                  defaultValue: "Turn on to add at the end of the list.",
                                })}
                              </p>
                              {disabledCodes.length > 0 ? (
                                <div className="client-lang-disabled-list">
                                  {disabledCodes.map((code) => (
                                    <div
                                      key={code}
                                      className="client-lang-disabled-row"
                                    >
                                      <span className="client-lang-label-with-flag">
                                        {getMenuLanguageFlag(code) ? (
                                          <img
                                            className="client-lang-flag"
                                            src={getMenuLanguageFlag(code)}
                                            alt=""
                                            width={24}
                                            height={18}
                                          />
                                        ) : null}
                                        <span className="client-lang-sortable-label text-truncate">
                                          {langLabel(code)}
                                        </span>
                                      </span>
                                      <Form.Check
                                        type="switch"
                                        id={`lang-disabled-${code}`}
                                        checked={false}
                                        className="client-lang-sortable-switch ms-2 mb-0"
                                        onChange={() => turnOnLanguage(code)}
                                      />
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="lang-list__empty">
                                  {t("languagesAllActive", {
                                    defaultValue: "All languages are enabled.",
                                  })}
                                </p>
                              )}
                            </section>
                          </div>
                                </>
                              );
                            })()}

                          {false && (
                            <>
                          {/* ---- welcome intro video ---- */}
                          <div className="mb-3 p-3 border rounded-3" style={{ borderColor: "#ffe3de", backgroundColor: "#fff" }}>
                            <label className="form-label text-primary">
                              {t("welcomeVideo")}
                            </label>
                            <div
                              style={{
                                fontSize: "0.9rem",
                                color: "#6c757d",
                                marginBottom: "10px",
                              }}
                            >
                              {t("welcomeVideoInfo")}
                            </div>
                            <div
                              style={{
                                display: "flex",
                                gap: 10,
                                alignItems: "flex-end",
                              }}
                            >
                              <div
                                style={{
                                  width: "280px",
                                  maxWidth: "100%",
                                  minHeight: "150px",
                                  position: "relative",
                                }}
                              >
                                {previewWelcomeVideo ? (
                                  <video
                                    src={previewWelcomeVideo}
                                    controls
                                    style={{
                                      width: "100%",
                                      borderRadius: 10,
                                      border: "2px solid #FA8072",
                                      background: "#000",
                                    }}
                                  />
                                ) : restaurantData?.welcomeVideo ? (
                                  <video
                                    src={getRestaurantUploadUrl(
                                      "restaurant",
                                      restaurantData.welcomeVideo
                                    )}
                                    controls
                                    style={{
                                      width: "100%",
                                      borderRadius: 10,
                                      border: "1px solid #ccc",
                                      background: "#000",
                                    }}
                                  />
                                ) : (
                                  <div
                                    style={{
                                      width: "100%",
                                      minHeight: "150px",
                                      borderRadius: 10,
                                      border: "1px dashed #ccc",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      color: "#999",
                                      fontSize: "0.9rem",
                                      padding: "1rem",
                                      textAlign: "center",
                                    }}
                                  >
                                    {t("noWelcomeVideo")}
                                  </div>
                                )}

                                <input
                                  id="welcome-video-upload"
                                  type="file"
                                  accept="video/*"
                                  style={{ display: "none" }}
                                  ref={welcomeVideoInputRef}
                                  onChange={handleWelcomeVideoChange}
                                />
                              </div>

                              <div
                                className="d-flex flex-column"
                                style={{
                                  gap: 8,
                                  alignItems: "center",
                                  justifyContent: "flex-end",
                                  minHeight: "150px",
                                }}
                              >
                                <label
                                  htmlFor="welcome-video-upload"
                                  style={{
                                    backgroundColor: "#FA8072",
                                    borderRadius: "50%",
                                    padding: 8,
                                    cursor: "pointer",
                                    color: "white",
                                    boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <FaPencilAlt size={15} />
                                </label>

                                {(restaurantData?.welcomeVideo || previewWelcomeVideo) && (
                                  <button
                                    type="button"
                                    onClick={async () => {
                                      const result = await Swal.fire({
                                        title: t("deleteWelcomeVideo"),
                                        icon: "question",
                                        showCancelButton: true,
                                        confirmButtonText: t("confirm"),
                                        cancelButtonText: t("cancel"),
                                      });
                                      if (!result.isConfirmed) return;
                                      try {
                                        await deleteRestaurantWelcomeVideo();
                                        setRestaurantData({
                                          ...restaurantData,
                                          welcomeVideo: null,
                                        });
                                        setPreviewWelcomeVideo(null);
                                        setSelectedWelcomeVideo(null);
                                        if (welcomeVideoInputRef.current) {
                                          welcomeVideoInputRef.current.value = "";
                                        }
                                        toast.success(t("welcomeVideoDeleted"));
                                      } catch (error) {
                                        toast.error(
                                          error?.message || t("errorUpdating")
                                        );
                                      }
                                    }}
                                    style={{
                                      backgroundColor: "#dc3545",
                                      borderRadius: "50%",
                                      padding: 8,
                                      cursor: "pointer",
                                      color: "white",
                                      border: "none",
                                      boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <FaTrash size={15} />
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>

                          {/*  ---- msg bienvenue ---- */}
                          <div className="mb-3">
                            {/* Titre + Other Languages à droite */}
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: "2px",
                              }}
                            >
                              <label
                                className="form-label text-primary"
                                style={{ marginBottom: "0px" }} // ← enlève le spacing automatique
                              >
                                {t("welcomeMessage")}
                              </label>

                              <span
                                onClick={() =>
                                  setShowOtherLanguages(!showOtherLanguages)
                                }
                                style={{
                                  cursor: "pointer",
                                  color: Colors.primary, // saumon
                                  fontSize: "0.9rem",
                                  fontWeight: 400,
                                  textDecoration: "underline",
                                }}
                              >
                                {showOtherLanguages
                                  ? t("hideOtherLanguages")
                                  : t("otherLanguages")}
                                &nbsp;
                                {showOtherLanguages ? "▲" : "▼"}
                              </span>
                            </div>

                            {/* Sous-texte */}
                            <div
                              style={{
                                fontSize: "0.9rem",
                                color: "#6c757d",
                                marginBottom: "10px",
                              }}
                            >
                              {t("welcomeMessageInfo")}
                            </div>

                            {/* Liste - 1er élément (EN) */}
                            {(() => {
                              const lang = "en";
                              const messageObj =
                                restaurantData?.msgBienvenue?.find(
                                  (m) => m.language === lang
                                ) || {
                                  language: lang,
                                  msg: "",
                                };

                              return (
                                <div
                                  key={lang}
                                  className="d-flex align-items-center"
                                  style={{ gap: "10px", marginBottom: "10px" }}
                                >
                                  <span
                                    className="d-flex align-items-center client-lang-label-with-flag"
                                    style={{
                                      width: "88px",
                                      flexShrink: 0,
                                      fontWeight: 500,
                                    }}
                                  >
                                    {getMenuLanguageFlag(lang) ? (
                                      <img
                                        className="client-lang-flag"
                                        src={getMenuLanguageFlag(lang)}
                                        alt=""
                                        width={24}
                                        height={18}
                                      />
                                    ) : null}
                                    {lang.toUpperCase()}
                                  </span>

                                  <input
                                    type="text"
                                    className="form-control"
                                    placeholder={t("message")}
                                    value={messageObj.msg}
                                    maxLength={100}
                                    onChange={(e) => {
                                      const newMessages = [
                                        ...(restaurantData?.msgBienvenue || []),
                                      ];
                                      const existingIndex =
                                        newMessages.findIndex(
                                          (m) => m.language === lang
                                        );
                                      if (existingIndex !== -1)
                                        newMessages[existingIndex].msg =
                                          e.target.value;
                                      else
                                        newMessages.push({
                                          language: lang,
                                          msg: e.target.value,
                                        });

                                      setRestaurantData({
                                        ...restaurantData,
                                        msgBienvenue: newMessages,
                                      });
                                    }}
                                  />

                                  <span
                                    style={{
                                      fontSize: "0.8rem",
                                      color: "#6c757d",
                                    }}
                                  >
                                    {messageObj.msg.length}/100
                                  </span>
                                </div>
                              );
                            })()}

                            {/* Autres langues si showOtherLanguages = true */}
                            {showOtherLanguages &&
                              ALL_CLIENT_LANG_CODES.filter(
                                (c) => c !== "en"
                              ).map((lang) => {
                                const messageObj =
                                  restaurantData?.msgBienvenue?.find(
                                    (m) => m.language === lang
                                  ) || {
                                    language: lang,
                                    msg: "",
                                  };

                                return (
                                  <div
                                    key={lang}
                                    className="d-flex align-items-center"
                                    style={{
                                      gap: "10px",
                                      marginBottom: "10px",
                                    }}
                                  >
                                    <span
                                      className="d-flex align-items-center client-lang-label-with-flag"
                                      style={{
                                        width: "88px",
                                        flexShrink: 0,
                                        fontWeight: 500,
                                      }}
                                    >
                                      {getMenuLanguageFlag(lang) ? (
                                        <img
                                          className="client-lang-flag"
                                          src={getMenuLanguageFlag(lang)}
                                          alt=""
                                          width={24}
                                          height={18}
                                        />
                                      ) : null}
                                      {lang.toUpperCase()}
                                    </span>

                                    <input
                                      type="text"
                                      className="form-control"
                                      placeholder={t("message")}
                                      value={messageObj.msg}
                                      maxLength={100}
                                      onChange={(e) => {
                                        const newMessages = [
                                          ...(restaurantData?.msgBienvenue ||
                                            []),
                                        ];
                                        const existingIndex =
                                          newMessages.findIndex(
                                            (m) => m.language === lang
                                          );

                                        if (existingIndex !== -1)
                                          newMessages[existingIndex].msg =
                                            e.target.value;
                                        else
                                          newMessages.push({
                                            language: lang,
                                            msg: e.target.value,
                                          });

                                        setRestaurantData({
                                          ...restaurantData,
                                          msgBienvenue: newMessages,
                                        });
                                      }}
                                    />

                                    <span
                                      style={{
                                        fontSize: "0.8rem",
                                        color: "#6c757d",
                                      }}
                                    >
                                      {messageObj.msg.length}/100
                                    </span>
                                  </div>
                                );
                              })}
                          </div>
                            </>
                          )}
                                </article>
                                </div>
                              </Tab.Pane>

                              <Tab.Pane eventKey="Storytelling">
                                <div className="resto-info-section storytelling-section">
                                  <StorytellingStepper
                                    restaurantData={restaurantData}
                                    setRestaurantData={setRestaurantData}
                                    previewStorytellingImages={previewStorytellingImages}
                                    uploadsServer={config.UPLOADS_SERVER}
                                    logoUrl={
                                      restaurantData?.logo
                                        ? `${config.UPLOADS_SERVER}/logos/${restaurantData.logo}`
                                        : null
                                    }
                                    menuButtonColor={restaurantData?.menuColors?.buttonColor}
                                    updateStorytellingField={updateStorytellingField}
                                    addStorytellingEntry={addStorytellingEntry}
                                    removeStorytellingEntry={removeStorytellingEntry}
                                    onStorytellingImageChange={handleStorytellingImageChange}
                                    onDeleteStorytellingImage={handleDeleteStorytellingImage}
                                    onSubmit={handleSubmitStorytellingChanges}
                                    isSubmitting={storytellingSubmitting}
                                  />
                                </div>
                              </Tab.Pane>

                              <Tab.Pane eventKey="Tax">
                                <div className="resto-info-section">
                                  <div className="mb-3">
                                    <label className="form-label text-primary">
                                      {restaurantData?.country?.toLowerCase() === "portugais" ? t("nif") : t("taxId")}
                                    </label>
                                    <input
                                      type="text"
                                      className="form-control"
                                      value={
                                        restaurantData?.country?.toLowerCase() === "portugais"
                                          ? restaurantData?.NIF || ""
                                          : restaurantData?.taxIdentification || ""
                                      }
                                      onChange={(e) => {
                                        if (restaurantData?.country?.toLowerCase() === "portugais") {
                                          setRestaurantData({ ...restaurantData, NIF: e.target.value });
                                        } else {
                                          setRestaurantData({ ...restaurantData, taxIdentification: e.target.value });
                                        }
                                      }}
                                    />
                                  </div>
                                  <div className="mb-3">
                                    <label className="form-label text-primary">{t("taxStyle")}</label>
                                    <select
                                      className="form-select"
                                      value={restaurantData?.taxStyle || "excluded"}
                                      onChange={(e) =>
                                        setRestaurantData({ ...restaurantData, taxStyle: e.target.value })
                                      }
                                    >
                                      <option value="excluded">{t("americainStyle")} ({t("taxExcluded")})</option>
                                      <option value="included">{t("europeanStyle")} ({t("taxIncluded")})</option>
                                    </select>
                                  </div>
                                  <div className="mb-3">
                                    <label className="form-label text-primary">{t("taxPercentage")}</label>
                                    {(restaurantData?.percentages?.length > 0 ? restaurantData.percentages : [{ name: "", value: 0 }]).map((p, index) => (
                                      <div className="d-flex align-items-center mb-2" key={index}>
                                        <input
                                          type="text"
                                          className="form-control me-2"
                                          placeholder={t("percentageName")}
                                          value={p.name}
                                          onChange={(e) => {
                                            const newPercentages =
                                              restaurantData.percentages?.length > 0
                                                ? [...restaurantData.percentages]
                                                : [{ name: "", value: 0 }];
                                            newPercentages[index].name = e.target.value;
                                            setRestaurantData({ ...restaurantData, percentages: newPercentages });
                                          }}
                                        />
                                        <input
                                          type="number"
                                          className="form-control me-2"
                                          placeholder="%"
                                          value={p.value}
                                          min={0}
                                          max={100}
                                          onChange={(e) => {
                                            const newPercentages =
                                              restaurantData.percentages?.length > 0
                                                ? [...restaurantData.percentages]
                                                : [{ name: "", value: 0 }];
                                            newPercentages[index].value = Number(e.target.value);
                                            setRestaurantData({ ...restaurantData, percentages: newPercentages });
                                          }}
                                        />
                                        <button
                                          type="button"
                                          className="btn btn-sm me-2"
                                          style={{ color: "#FA8072", borderColor: "#FA8072" }}
                                          onClick={() => {
                                            const newPercentages = [...(restaurantData.percentages || [])];
                                            newPercentages.splice(index, 1);
                                            setRestaurantData({ ...restaurantData, percentages: newPercentages });
                                          }}
                                        >
                                          <FaTrash />
                                        </button>
                                      </div>
                                    ))}
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-primary mt-1"
                                      onClick={() =>
                                        setRestaurantData({
                                          ...restaurantData,
                                          percentages: [...(restaurantData.percentages || []), { name: "", value: 0 }],
                                        })
                                      }
                                    >
                                      {t("addPercentage")}
                                    </button>
                                  </div>
                                </div>
                              </Tab.Pane>
                            </Tab.Content>
                          </Tab.Container>
                          {activeRestoSubTab !== "Storytelling" ? (
                            <div className="resto-info-actions">
                              <button
                                className="btn btn-primary"
                                type="button"
                                onClick={handleSubmitRestaurantChanges}
                              >
                                {t("submitChanges")}
                              </button>
                            </div>
                          ) : null}
                        </div>
                      </Tab.Pane>

                      <Tab.Pane id="profile-settings" eventKey="Setting">
                        <div className="app-profile-account-panel pt-1">
                          <div className="app-profile-account-panel__head">
                            <p className="app-profile-account-panel__eyebrow">
                              {t("password")}
                            </p>
                            <h4 className="app-profile-account-panel__title">
                              {t("accountSettings")}
                            </h4>
                            <p className="app-profile-account-panel__desc">
                              {t("accountSettingsHelp", {
                                defaultValue:
                                  "Change your password to keep your account secure.",
                              })}
                            </p>
                          </div>

                          <div className="app-profile-account-panel__body settings-form">
                            <form onSubmit={handleUpdatePassword}>
                              {error && (
                                <div className="alert alert-danger app-profile-alert">
                                  {error}
                                </div>
                              )}

                              <div className="row g-3">
                                <div className="form-group col-md-12">
                                  <label className="form-label">
                                    {t("oldPwd")}
                                  </label>
                                  <div className="position-relative app-profile-password-field">
                                    <input
                                      type={
                                        showOldPassword ? "text" : "password"
                                      }
                                      placeholder={t("password")}
                                      className="form-control"
                                      onChange={(e) =>
                                        setOldPassword(e.target.value)
                                      }
                                      value={oldPassword}
                                    />
                                    <span
                                      className={`show-pass eye ${
                                        showOldPassword ? "active" : ""
                                      }`}
                                      onClick={() =>
                                        setShowOldPassword(!showOldPassword)
                                      }
                                    >
                                      <i className="fa fa-eye-slash" />
                                      <i className="fa fa-eye" />
                                    </span>
                                  </div>
                                </div>
                                <div className="form-group col-md-12">
                                  <label className="form-label">
                                    {t("newPwd")}
                                  </label>
                                  <div className="position-relative app-profile-password-field">
                                    <input
                                      type={showPassword ? "text" : "password"}
                                      placeholder={t("newPwd")}
                                      className="form-control"
                                      onChange={handlePasswordChange}
                                      value={password}
                                      autoComplete="new-password"
                                    />
                                    <span
                                      className={`show-pass eye ${
                                        showPassword ? "active" : ""
                                      }`}
                                      onClick={() =>
                                        setShowPassword(!showPassword)
                                      }
                                    >
                                      <i className="fa fa-eye-slash" />
                                      <i className="fa fa-eye" />
                                    </span>
                                  </div>
                                  <PasswordStrengthMeter
                                    password={password}
                                    labels={{
                                      weak: t("passwordStrengthWeak", {
                                        defaultValue: "Weak",
                                      }),
                                      medium: t("passwordStrengthMedium", {
                                        defaultValue: "Medium",
                                      }),
                                      strong: t("passwordStrengthStrong", {
                                        defaultValue: "Strong",
                                      }),
                                    }}
                                  />
                                </div>
                                <div className="form-group col-md-12">
                                  <label className="form-label">
                                    {t("confirmPwd")}
                                  </label>
                                  <div className="position-relative app-profile-password-field">
                                    <input
                                      type={
                                        showConfirmPassword
                                          ? "text"
                                          : "password"
                                      }
                                      placeholder={t("confirmPwd")}
                                      className="form-control"
                                      onChange={(e) =>
                                        setConfirmPassword(e.target.value)
                                      }
                                      value={confirmPassword}
                                    />
                                    <span
                                      className={`show-pass eye ${
                                        showConfirmPassword ? "active" : ""
                                      }`}
                                      onClick={() =>
                                        setShowConfirmPassword(
                                          !showConfirmPassword
                                        )
                                      }
                                    >
                                      <i className="fa fa-eye-slash" />
                                      <i className="fa fa-eye" />
                                    </span>
                                  </div>
                                </div>
                              </div>

                              <div className="app-profile-account-panel__actions text-end">
                                <button
                                  className="btn btn-primary"
                                  type="submit"
                                  disabled={loading}
                                >
                                  {loading ? t("updating") : t("submitChanges")}
                                </button>
                              </div>
                            </form>
                          </div>
                        </div>
                      </Tab.Pane>

                      <Tab.Pane id="profile-2fa" eventKey="TwoFactor">
                        <TwoFactorSettings
                          twoFactorEnabled={twoFactorEnabled}
                          onEnabledChange={setTwoFactorEnabled}
                          isActive={activeMainTab === "TwoFactor"}
                        />
                      </Tab.Pane>
                    </Tab.Content>
                    </div>
                  </Tab.Container>
      </div>
      </div>

      {/* send Modal */}
      <Modal
        className="modal fade"
        show={state.sendMessage}
        onHide={() => dispatch({ type: "sendMessage" })}
        centered
      >
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{t("modalSendMessageTitle")}</h5>
            <Button
              variant=""
              type="button"
              className="btn-close"
              onClick={() => dispatch({ type: "sendMessage" })}
            ></Button>
          </div>
          <div className="modal-body">
            <form
              className="comment-form"
              onSubmit={(e) => {
                e.preventDefault();
                dispatch({ type: "sendMessage" });
              }}
            >
              <div className="row">
                <div className="col-lg-6">
                  <div className="form-group mb-3">
                    <label htmlFor="author" className="form-label">
                      {" "}
                      {t("labelName")} <span className="required">*</span>{" "}
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      defaultValue=""
                      name="Author"
                      placeholder={t("placeholderAuthor")}
                    />
                  </div>
                </div>
                <div className="col-lg-6">
                  <div className="form-group mb-3">
                    <label htmlFor="email" className="form-label">
                      {" "}
                      {t("email")} <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      defaultValue=""
                      placeholder={t("email")}
                      name="Email"
                    />
                  </div>
                </div>
                <div className="col-lg-12">
                  <div className="form-group mb-3">
                    <label htmlFor="comment" className="form-label">
                      {t("formComment")}
                    </label>
                    <textarea
                      rows={4}
                      className="form-control"
                      name="comment"
                      placeholder={t("formComment")}
                      defaultValue={""}
                    />
                  </div>
                </div>
                <div className="col-lg-12">
                  <div className="form-group mb-3">
                    <input
                      type="submit"
                      value={t("postComment")}
                      className="submit btn btn-primary"
                      name="submit"
                    />
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </Modal>
      {/* Post Modal */}
      <Modal
        show={state.post}
        className="modal fade"
        id="postModal"
        onHide={() => dispatch({ type: "postModal" })}
        centered
      >
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{t("modalPostTitle")}</h5>
            <Button
              variant=""
              type="button"
              className="btn-close"
              onClick={() => dispatch({ type: "postModal" })}
            ></Button>
          </div>
          <div className="modal-body">
            <textarea
              name="textarea"
              id="textarea"
              cols={30}
              rows={5}
              className="form-control mb-2 bg-transparent"
              placeholder={t("placeholderWhatYouWant")}
              defaultValue={""}
            />
            <Link
              className="btn btn-primary btn-rounded mt-1"
              to="/app-profile"
            >
              {t("modalPostTitle")}
            </Link>
          </div>
        </div>
      </Modal>
      {/* Link Modal */}
      <Modal
        show={state.link}
        className="modal fade post-input"
        id="linkModal"
        onHide={() => dispatch({ type: "linkModal" })}
        centered
      >
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{t("modalSocialLinksTitle")}</h5>
            <button
              type="button"
              className="btn-close"
              onClick={() => dispatch({ type: "linkModal" })}
            ></button>
          </div>
          <div className="modal-body">
            <Link className="btn-social me-1 facebook" to="/app-profile">
              <i className="fab fa-facebook-f" />
            </Link>
            <Link className="btn-social me-1 google-plus" to="/app-profile">
              {" "}
              <i className="fab fa-google-plus" />
            </Link>
            <Link className="btn-social me-1 linkedin" to="/app-profile">
              <i className="fab fa-linkedin-in" />
            </Link>
            <Link className="btn-social me-1 instagram" to="/app-profile">
              {" "}
              <i className="fab fa-instagram" />
            </Link>
            <Link className="btn-social me-1 twitter" to="/app-profile">
              <i className="fab fa-twitter" />
            </Link>
            <Link className="btn-social me-1 youtube" to="/app-profile">
              <i className="fab fa-youtube" />
            </Link>
            <Link className="btn-social whatsapp" to="/app-profile">
              <i className="fab fa-whatsapp" />
            </Link>
          </div>
        </div>
      </Modal>
      {/* Camera Modal */}
      <Modal
        show={state.camera}
        className="modal fade"
        id="cameraModal"
        onHide={() => dispatch({ type: "cameraModal" })}
        centered
      >
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{t("modalUploadImagesTitle")}</h5>
            <button
              type="button"
              className="btn-close"
              onClick={() => dispatch({ type: "cameraModal" })}
            ></button>
          </div>
          <div className="modal-body">
            <div className="input-group custom_file_input mb-3">
              <span className="input-group-text">{t("uploadAction")}</span>
              <div className="form-file">
                <input type="file" className="form-file-input form-control" />
              </div>
            </div>
          </div>
        </div>
      </Modal>
      {/* Reply Modal */}
      <Modal
        show={state.reply}
        className="modal fade"
        id="replyModal"
        onHide={() => dispatch({ type: "replyModal" })}
        centered
      >
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{t("modalPostReplyTitle")}</h5>
            <button
              type="button"
              className="btn-close"
              onClick={() => dispatch({ type: "replyModal" })}
            ></button>
          </div>
          <div className="modal-body">
            <form>
              <textarea className="form-control" rows="4" placeholder={t("messagePlaceholder")} defaultValue="" />
            </form>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-danger light"
              onClick={() => dispatch({ type: "replyModal" })}
            >
              {t("close")}
            </button>
            <button type="button" className="btn btn-primary">
              {t("replyVerb")}
            </button>
          </div>
        </div>
      </Modal>
      {videoSlice && (
        <WelcomeVideoSliceModal
          url={videoSlice.url}
          duration={videoSlice.duration}
          onCancel={() => {
            if (videoSlice.url) URL.revokeObjectURL(videoSlice.url);
            setVideoSlice(null);
          }}
          onConfirm={(start, end) => {
            applyVideoSlice(
              videoSlice.kind,
              videoSlice.file,
              videoSlice.url,
              start,
              end
            );
            setVideoSlice(null);
          }}
        />
      )}
    </Fragment>
  );
};

export default AppProfile;